import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  TextField,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  Route,
  Schedule,
  Directions,
  People,
  Edit,
  Delete,
  ExpandMore,
  CheckCircle,
  Cancel,
  SwapHoriz,
  CalendarToday,
  LocationOn,
  Phone,
  Email,
  Notes,
  Visibility,
  VisibilityOff,
  ContentCopy,
  Warning,
} from '@mui/icons-material';
import { apiService } from '../services/api';
import { isSameCalendarDate } from '../utils/dateUtils';
import { getServiceFrequency as getServiceFrequencyUtil } from '../utils/serviceDateUtils';
import InfoTip from './InfoTip';

interface RouteOptimizationDashboardProps {
  providerId: string;
}

interface ScheduledService {
  _id: string;
  customerId: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    servicePricing?: {
      lawnMowingTrimming?: any;
      weedControlFertilizing?: any;
      snowRemoval?: any;
    };
  };
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    coordinates?: { lat: number; lng: number };
  };
  serviceType?: {
    id: string;
    name: string;
    category: string;
    description?: string;
  };
  scheduledDate: string;
  scheduledTime?: string;
  estimatedDuration?: number;
  status: string;
  frequency?: string;
  acceptedBidId?: {
    servicePricing: Array<{
      frequency: string;
      recurringDayOfWeek?: number;
      price: number;
      service?: string;
      serviceName?: string;
      seasonalContractDetails?: {
        serviceFrequency?: 'weekly' | 'bi-weekly' | 'monthly' | 'per-instance';
        contractStartDate?: string;
        contractEndDate?: string;
        totalContractPrice?: string;
      };
    }>;
  };
  acceptedBid?: {
    servicePricing: Array<{
      frequency: string;
      recurringDayOfWeek?: number;
      price: number;
      service?: string;
      serviceName?: string;
      seasonalContractDetails?: {
        serviceFrequency?: 'weekly' | 'bi-weekly' | 'monthly' | 'per-instance';
        contractStartDate?: string;
        contractEndDate?: string;
        totalContractPrice?: string;
      };
    }>;
  };
  notes?: string;
  isOnPaymentHold?: boolean;
  onHoldReason?: string | null;
}

interface OptimizedRoute {
  crewId: string;
  crewName: string;
  crewSize: number;
  jobs: Array<{
    customerId: string;
    customerName: string;
    address: string;
    services: any;
    estimatedDuration: number;
    estimatedArrival: string;
    estimatedDeparture: string;
    coordinates: any;
    preferredTime?: string;
    notes?: string;
    sequence: number;
  }>;
  totalTravelTime: number;
  totalServiceTime: number;
  totalDistance: number;
}

interface CompletedRoute {
  _id: string;
  date: string;
  crewId: string;
  crewSize: number;
  jobs: Array<{
    customerId: string;
    customerName: string;
    address: string;
    services: any;
    completed: boolean;
    actualArrival?: string;
    actualDeparture?: string;
    notes?: string;
  }>;
  totalTravelTime: number;
  totalServiceTime: number;
  totalDistance: number;
  actualStartTime?: string;
  actualEndTime?: string;
}

interface CrewLogin {
  id: string;
  crewId: string;
  crewNumber: number;
  email: string;
  firstName: string;
  lastName: string;
  password?: string | null;
  message?: string;
  loginUrl?: string;
}

const CREW_LOGIN_URL =
  process.env.REACT_APP_CREW_LOGIN_URL || 'https://www.illinoislawnhub.com/crew/login';

const RouteOptimizationDashboard: React.FC<RouteOptimizationDashboardProps> = ({ providerId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Date and route state
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [crewSize, setCrewSize] = useState(1);
  const [scheduledServices, setScheduledServices] = useState<ScheduledService[]>([]);
  const [optimizedRoutes, setOptimizedRoutes] = useState<OptimizedRoute[]>([]);
  const [completedRoutes, setCompletedRoutes] = useState<CompletedRoute[]>([]);
  // SINGLE SOURCE OF TRUTH: Store full ExistingCustomer objects (same as Customer Details dialog)
  const [existingCustomersMap, setExistingCustomersMap] = useState<Map<string, any>>(new Map());
  
  // Crew-service assignments state (edited in Crew Management; loaded here for Sort and Optimize)
  const [crewServiceAssignments, setCrewServiceAssignments] = useState<Record<number, string[]>>({});
  
  // Helper to check if a job should be displayed in routes
  // Returns false if the service is not scheduled for the selected date (but shows completed services)
  const shouldDisplayJob = useCallback((job: OptimizedRoute['jobs'][0]): boolean => {
    if (!selectedDate) return true;
    
    // If no scheduledServices loaded yet, show all jobs (they'll be filtered when services load)
    if (scheduledServices.length === 0) {
      console.log('[SHOULD-DISPLAY-JOB] No scheduledServices loaded yet, showing job:', job.customerId);
      return true;
    }
    
    // Find the service request for this job
    // IMPORTANT: job.customerId is the ServiceRequest _id (not the customer User _id)
    const service = scheduledServices.find(s => {
      // Handle both string and object ID comparisons
      const serviceId = s._id?.toString() || String(s._id || '').trim();
      const jobId = job.customerId?.toString() || String(job.customerId || '').trim();
      const matches = serviceId === jobId;
      
      if (!matches && serviceId && jobId) {
        // Try comparing as strings after normalizing
        const normalizedServiceId = String(serviceId).trim();
        const normalizedJobId = String(jobId).trim();
        return normalizedServiceId === normalizedJobId;
      }
      return matches;
    });
    
    if (!service) {
      // If service not found, keep it (might be manually added or service was deleted)
      console.log('[SHOULD-DISPLAY-JOB] Service not found for job, showing anyway:', {
        jobCustomerId: job.customerId,
        availableServiceIds: scheduledServices.map(s => s._id?.toString() || s._id)
      });
      return true;
    }
    
    console.log('[SHOULD-DISPLAY-JOB] Service found, checking if should display:', {
      jobCustomerId: job.customerId,
      serviceId: service._id,
      serviceScheduledDate: service.scheduledDate,
      selectedDate: selectedDate?.toISOString()
    });
    
    // Show completed services (they'll have a badge)
    // Don't filter them out
    
    // Check if service is scheduled for the selected date
    // IMPORTANT: Use standardized date comparison utilities for consistency
    // This prevents timezone boundary issues
    if (service.scheduledDate && selectedDate) {
      // CRITICAL: selectedDate is created from a date input as a local date at midnight
      // But we need to compare it as a calendar date (UTC midnight)
      // Extract the calendar date components from selectedDate (local date input)
      // Then create a UTC date for comparison
      const selectedYear = selectedDate.getFullYear();
      const selectedMonth = selectedDate.getMonth();
      const selectedDay = selectedDate.getDate();
      const selectedDateUTC = new Date(Date.UTC(selectedYear, selectedMonth, selectedDay, 0, 0, 0, 0));
      
      // Use standardized date comparison
      const matches = isSameCalendarDate(service.scheduledDate, selectedDateUTC);
      
      console.log('[SHOULD-DISPLAY-JOB] Date comparison:', {
        jobCustomerId: job.customerId,
        serviceScheduledDate: service.scheduledDate,
        selectedDate: selectedDate?.toISOString(),
        selectedDateLocal: `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`,
        selectedDateUTC: selectedDateUTC.toISOString(),
        matches
      });
      
      // Show if scheduled for the selected date (including completed ones)
      return matches;
    }
    
    // If no scheduled date, don't show it (service was rescheduled or cancelled)
    return false;
  }, [selectedDate, scheduledServices]);
  
  // Helper to check if a job is completed
  const isJobCompleted = useCallback((job: OptimizedRoute['jobs'][0]): boolean => {
    if (!scheduledServices.length) return false;
    
    const service = scheduledServices.find(s => {
      // Handle both string and object ID comparisons
      const serviceId = s._id?.toString() || s._id;
      const jobId = job.customerId?.toString() || job.customerId;
      const matches = serviceId === jobId;
      if (!matches && serviceId && jobId) {
        // Try comparing as strings after normalizing
        const normalizedServiceId = String(serviceId).trim();
        const normalizedJobId = String(jobId).trim();
        return normalizedServiceId === normalizedJobId;
      }
      return matches;
    });
    return service?.status === 'completed' || false;
  }, [scheduledServices]);
  
  // Handler to delete a job from a route
  const handleDeleteJob = useCallback((job: OptimizedRoute['jobs'][0], route: OptimizedRoute) => {
    const updatedRoutes = optimizedRoutes.map(r => {
      if (r.crewId === route.crewId) {
        const updatedJobs = r.jobs.filter(j => j.customerId !== job.customerId);
        // Recalculate totals
        const totalServiceTime = updatedJobs.reduce((sum: number, j) => sum + (j.estimatedDuration || 60), 0);
        return {
          ...r,
          jobs: updatedJobs,
          totalServiceTime: totalServiceTime
        };
      }
      return r;
    });
    setOptimizedRoutes(updatedRoutes);
    setSuccess(`Removed ${job.customerName} from ${route.crewName}`);
  }, [optimizedRoutes]);
  
  // Cache for fetched services (to avoid repeated API calls)
  const [fetchedServicesCache, setFetchedServicesCache] = useState<Map<string, any>>(new Map());

  // Helper to normalize service display names for consistent UI display
  const getServiceDisplayName = useCallback((serviceType: any): string => {
    if (!serviceType) return 'N/A';
    
    // Normalize mowing services to show "Lawn Mowing/Trimming"
    const category = serviceType.category || serviceType.id || '';
    const name = serviceType.name || '';
    
    // Check if it's a mowing service (by category or name)
    if (category === 'mowing' || 
        category === 'lawn_mowing' || 
        category === 'lawnMowingTrimming' ||
        name.toLowerCase().includes('mowing') ||
        name.toLowerCase().includes('trim') ||
        name.toLowerCase().includes('edge')) {
      return 'Lawn Mowing/Trimming';
    }
    
    // Other service types
    if (category === 'fertilizing' || category === 'weed_control' || category === 'weedControlFertilizing') {
      return 'Weed Control/Fertilizing';
    }
    if (category === 'snow_removal' || category === 'snowRemoval') {
      return 'Snow Removal';
    }
    if (category === 'additional_services' || category === 'additionalServices') {
      return name || 'Additional Services';
    }
    
    // Fallback to original name if no match
    return name || category || 'Service';
  }, []);

  const toServiceFlags = useCallback((serviceType: any) => {
    const category = String(serviceType?.category || serviceType?.id || '');
    const name = String(serviceType?.name || '').toLowerCase();
    const flags = {
      lawnMowingTrimming: false,
      weedControlFertilizing: false,
      snowRemoval: false,
      additionalServices: false,
    };
    const c = category.toLowerCase();
    if (c === 'mowing' || c.includes('mowing') || c.includes('lawn') || name.includes('mowing')) {
      flags.lawnMowingTrimming = true;
    } else if (c === 'fertilizing' || c.includes('fertiliz') || c.includes('weed') || name.includes('fertiliz') || name.includes('weed')) {
      flags.weedControlFertilizing = true;
    } else if (c === 'snow_removal' || c === 'snowremoval' || c.includes('snow') || name.includes('snow')) {
      flags.snowRemoval = true;
    } else if (c === 'additional_services' || c === 'additionalservices' || c.includes('additional') || name.includes('additional')) {
      flags.additionalServices = true;
    }
    return flags;
  }, []);

  // Helper to get service names from scheduledServices (same source as Today's Jobs)
  // job.customerId is the ServiceRequest _id
  const getServiceNames = useCallback((job: OptimizedRoute['jobs'][0]): string[] => {
    const serviceNames: string[] = [];
    
    // PRIMARY SOURCE: Use scheduledServices (same endpoint as Today's Jobs)
    // This is the most reliable source with correct serviceType data
    if (scheduledServices.length > 0) {
      const jobId = job.customerId?.toString() || job.customerId;
      const service = scheduledServices.find(s => {
        // Handle both string and object ID comparisons
        const serviceId = s._id?.toString() || s._id;
        const matches = serviceId === jobId;
        if (!matches && serviceId && jobId) {
          // Try comparing as strings after normalizing
          const normalizedServiceId = String(serviceId).trim();
          const normalizedJobId = String(jobId).trim();
          return normalizedServiceId === normalizedJobId;
        }
        return matches;
      });
      
      if (service?.serviceType) {
        // Use normalized display name for consistent UI
        const displayName = getServiceDisplayName(service.serviceType);
        serviceNames.push(displayName);
      }
    }
    
    // FALLBACK 1: Check if job.services is a serviceType object (what backend returns in routes)
    if (serviceNames.length === 0 && job.services && typeof job.services === 'object') {
      // Check if it's a serviceType object (has category, id, or name)
      if (job.services.category || job.services.id || job.services.name) {
        // Use normalized display name for consistent UI
        const displayName = getServiceDisplayName(job.services);
        serviceNames.push(displayName);
      } else if ('lawnMowingTrimming' in job.services || 'weedControlFertilizing' in job.services || 'snowRemoval' in job.services || 'additionalServices' in job.services) {
        // Check if it's the boolean flags format
        if (job.services.lawnMowingTrimming) serviceNames.push('Lawn Mowing/Trimming');
        if (job.services.weedControlFertilizing) serviceNames.push('Weed Control/Fertilizing');
        if (job.services.snowRemoval) serviceNames.push('Snow Removal');
        if (job.services.additionalServices) serviceNames.push('Additional Services');
      }
    }
    
    // FALLBACK 2: If still no service names, try to find service by customer name match
    // This handles cases where the service isn't in scheduledServices (e.g., completed, different date, manually added via "ADD TO TODAY")
    if (serviceNames.length === 0 && job.customerId && job.customerName && scheduledServices.length > 0) {
      // Try to find service by matching customer name
      // If customer has multiple services, prefer one scheduled for today (selectedDate)
      const servicesByCustomer = scheduledServices.filter(s => {
        const customerFirstName = s.customerId?.firstName || '';
        const customerLastName = s.customerId?.lastName || '';
        const fullName = `${customerFirstName} ${customerLastName}`.trim();
        return fullName === job.customerName;
      });
      
      // If multiple services found, prefer one scheduled for the selected date
      let serviceByCustomer = servicesByCustomer.find(s => {
        if (!selectedDate || !s.scheduledDate) return false;
        const scheduledDate = new Date(s.scheduledDate);
        const selectedDateStr = selectedDate.toISOString().split('T')[0];
        const scheduledDateStr = scheduledDate.toISOString().split('T')[0];
        return scheduledDateStr === selectedDateStr;
      });
      
      // If no service scheduled for selected date, use the first one
      if (!serviceByCustomer && servicesByCustomer.length > 0) {
        serviceByCustomer = servicesByCustomer[0];
      }
      
      if (serviceByCustomer?.serviceType) {
        // Use normalized display name for consistent UI
        const displayName = getServiceDisplayName(serviceByCustomer.serviceType);
        serviceNames.push(displayName);
      }
    }
    
    return serviceNames.length > 0 ? serviceNames : ['Service'];
  }, [scheduledServices, selectedDate, getServiceDisplayName]);
  
  // UI state
  const [optimizing, setOptimizing] = useState(false);
  const [loadingServices, setLoadingServices] = useState(false);
  const [rescheduleDialog, setRescheduleDialog] = useState<{
    open: boolean;
    service: ScheduledService | null;
    newDate: string;
    newTime?: string;
    changeType: 'one-time' | 'permanent';
  }>({
    open: false,
    service: null,
    newDate: '',
    newTime: '',
    changeType: 'one-time'
  });
  const [moveJobDialog, setMoveJobDialog] = useState<{
    open: boolean;
    job: any;
    fromRoute: OptimizedRoute | null;
    targetRouteIndex: number;
    targetPosition: string;
  }>({
    open: false,
    job: null,
    fromRoute: null,
    targetRouteIndex: 0,
    targetPosition: '1'
  });
  const [assignToRouteDialog, setAssignToRouteDialog] = useState<{
    open: boolean;
    service: ScheduledService | null;
    selectedRouteId: string;
  }>({
    open: false,
    service: null,
    selectedRouteId: ''
  });
  const [editingCrewName, setEditingCrewName] = useState<string | null>(null);
  // Crew Login Credentials - Clean State Management
  const [crewLoginsDialog, setCrewLoginsDialog] = useState<{ open: boolean }>({ open: false });
  const [crewLogins, setCrewLogins] = useState<CrewLogin[]>([]);
  const [loadingCrewLogins, setLoadingCrewLogins] = useState(false);
  const [editingLogin, setEditingLogin] = useState<{ crewId: string; crewName: string; password?: string } | null>(null);
  const [savingCredentials, setSavingCredentials] = useState(false);
  const [existingCrews, setExistingCrews] = useState<Record<number, { firstName: string; lastName: string; crewName: string }>>({});

  // Load existing crews on mount and when crew logins change
  useEffect(() => {
    const loadExistingCrews = async () => {
      try {
        const response = await apiService.get('/route-optimization/crew-logins');
        if (response.success && Array.isArray(response.data)) {
          const crewsMap: Record<number, { firstName: string; lastName: string; crewName: string }> = {};
          response.data.forEach((crew: any) => {
            const crewNumber = crew.crewNumber || parseInt(crew.crewId?.replace('crew-', '') || '1', 10);
            const firstName = crew.firstName || `Crew ${crewNumber}`;
            const lastName = crew.lastName || '';
            crewsMap[crewNumber] = {
              firstName,
              lastName,
              crewName: `${firstName} ${lastName}`.trim()
            };
          });
          setExistingCrews(crewsMap);
        }
      } catch (err) {
        console.error('[ROUTE-OPT] Failed to load existing crews:', err);
      }
    };
    loadExistingCrews();
  }, [providerId]);
  
  // Also reload existing crews when crew logins are updated
  useEffect(() => {
    if (crewLogins.length > 0) {
      const crewsMap: Record<number, { firstName: string; lastName: string; crewName: string }> = {};
      crewLogins.forEach((crew: CrewLogin) => {
        const crewNumber = crew.crewNumber;
        const firstName = crew.firstName || `Crew ${crewNumber}`;
        const lastName = crew.lastName || '';
        crewsMap[crewNumber] = {
          firstName,
          lastName,
          crewName: `${firstName} ${lastName}`.trim()
        };
      });
      setExistingCrews(prev => ({ ...prev, ...crewsMap }));
    }
  }, [crewLogins]);

  // Update route crew names when existing crews are loaded
  // BUT: PRESERVE saved crew names - only update if route is using default name
  useEffect(() => {
    if (Object.keys(existingCrews).length > 0 && optimizedRoutes.length > 0) {
      const updatedRoutes = optimizedRoutes.map(route => {
        const crewNumber = parseInt(route.crewId.replace('crew-', ''), 10);
        const existingCrew = existingCrews[crewNumber];
        if (existingCrew) {
          // Only update if route is using default name - preserve explicit saved crew names
          const isDefaultName = !route.crewName || route.crewName === route.crewId || route.crewName === `Crew ${crewNumber}`;
          if (isDefaultName && route.crewName !== existingCrew.crewName) {
            return { ...route, crewName: existingCrew.crewName };
          }
        }
        return route;
      });
      
      // Only update if something actually changed
      const hasChanges = updatedRoutes.some((route, index) => 
        route.crewName !== optimizedRoutes[index]?.crewName
      );
      
      if (hasChanges) {
        setOptimizedRoutes(updatedRoutes);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingCrews]); // Only run when existingCrews changes


  const normalizeCrewLogin = (entry: any): CrewLogin => {
    if (!entry) {
      return {
        id: Math.random().toString(36).slice(2),
        crewId: 'crew-1',
        crewNumber: 1,
        email: '',
        firstName: 'Crew',
        lastName: 'Member',
        password: null,
        message: ''
      };
    }
    const crewNumberFromId = entry.crewId ? parseInt(entry.crewId.replace('crew-', ''), 10) : undefined;
    const crewNumber = entry.crewNumber || (Number.isFinite(crewNumberFromId) ? crewNumberFromId : 1);
    const crewId = entry.crewId || `crew-${crewNumber}`;
    const firstName = entry.firstName || `Crew ${crewNumber}`;
    const lastName = entry.lastName || '';
    return {
      id: entry.id || entry._id || crewId,
      crewId,
      crewNumber,
      email: entry.email,
      firstName,
      lastName,
      password: entry.password ?? entry.crewLoginPassword ?? null,
      message: entry.message,
      loginUrl: entry.loginUrl || CREW_LOGIN_URL
    };
  };

  const normalizeCrewLogins = (entries: any[]): CrewLogin[] =>
    entries.map(normalizeCrewLogin).sort((a, b) => a.crewNumber - b.crewNumber);

  const fetchCompletedRoutes = useCallback(async (preserveOptimizedRoutes: boolean = false) => {
    if (!selectedDate) return;
    
    try {
      // Format date as YYYY-MM-DD in local timezone (not UTC)
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      const response = await apiService.get(`/route-optimization/routes/${dateStr}`);
      
      if (response.success && response.data && typeof response.data === 'object') {
        const data = response.data as any;
        
        // Load saved routes for current or future dates to display them in the optimized routes section
        if ((data.type === 'current' || data.type === 'future') && data.routes && data.routes.length > 0) {
          // Convert saved ScheduledRoute format to OptimizedRoute format
          const convertedRoutes = data.routes.map((route: any) => {
            const crewNumber = parseInt(route.crewId.replace('crew-', ''), 10);
            // CRITICAL: Use crew LOGIN name (existingCrews) so display matches actual crew user.
            // crew-1 = first crew user (Lawn), crew-2 = second (Snow), etc. Otherwise we show saved
            // route name (e.g. "Snow" on crew-1) but Save Routes saves to crewNumber 1 = Lawn user.
            let crewName = (existingCrews && existingCrews[crewNumber]?.crewName) || route.crewName || route.crewId;

            return {
              crewId: route.crewId,
              crewName: crewName,
              crewSize: route.crewSize,
              jobs: route.jobs.map((job: any, idx: number) => ({
              customerId: job.customerId,
              customerName: job.customerName,
              address: job.address,
              services: job.services,
              estimatedDuration: job.estimatedDuration || 60,
              estimatedArrival: job.estimatedArrival || '',
              estimatedDeparture: job.estimatedDeparture || '',
              coordinates: job.coordinates || { lat: 0, lng: 0 },
              preferredTime: job.preferredTime,
              notes: job.notes,
              sequence: idx + 1
            })),
            totalTravelTime: route.totalTravelTime || 0,
            totalServiceTime: route.totalServiceTime || 0,
            totalDistance: route.totalDistance || 0
            };
          });
          setOptimizedRoutes(convertedRoutes);
          // Keep "Number of crews" in sync so refresh doesn't revert to 1 crew (dropdown + Assignments dialog)
          setCrewSize(data.routes.length);
          setCompletedRoutes([]);
        } else if (data.type === 'historical' && data.routes) {
          setCompletedRoutes(data.routes);
          // Only clear optimized routes if we're not preserving them
          if (!preserveOptimizedRoutes) {
            console.log('[FETCH-ROUTES] Clearing optimized routes (historical routes found, preserveOptimizedRoutes=false)');
            setOptimizedRoutes([]);
          } else {
            console.log('[FETCH-ROUTES] Preserving optimized routes (preserveOptimizedRoutes=true)');
          }
        } else {
          setCompletedRoutes([]);
          // Don't clear optimized routes if they exist - they may have been just optimized but not saved yet
          // Use functional update to preserve existing routes when no saved routes are found
          setOptimizedRoutes((prevRoutes) => {
            // Always preserve routes if they exist and no saved routes were found
            // This prevents clearing routes when crew logins dialog closes or when existingCrews updates
            if (prevRoutes.length > 0) {
              console.log('[FETCH-ROUTES] Preserving existing routes:', prevRoutes.length, 'routes with', prevRoutes.reduce((sum, r) => sum + r.jobs.length, 0), 'jobs');
              return prevRoutes;
            }
            // Only clear if we don't have any routes (initial state)
            console.log('[FETCH-ROUTES] No routes to preserve, clearing');
            return [];
          });
        }
      }
    } catch (err: any) {
      console.error('Failed to fetch completed routes:', err);
    }
  }, [selectedDate, existingCrews]); // Depend on existingCrews so it always uses latest crew names

  // Load crew-service assignments on mount
  useEffect(() => {
    const loadAssignments = async () => {
      try {
        const response = await apiService.get('/route-optimization/crew-service-assignments');
        if (response.success && response.data) {
          const assignmentsMap: Record<number, string[]> = {};
          const assignments = Array.isArray(response.data) ? response.data : (response.data as any).data || [];
          assignments.forEach((assignment: any) => {
            assignmentsMap[assignment.crewNumber] = assignment.serviceTypes || [];
          });
          setCrewServiceAssignments(assignmentsMap);
        }
      } catch (err) {
        console.error('Failed to load crew-service assignments:', err);
      }
    };
    loadAssignments();
  }, []);

  // Update assignments when crewSize changes
  useEffect(() => {
    // Initialize assignments for new crews (if crewSize increased)
    setCrewServiceAssignments(prev => {
      const updated = { ...prev };
      for (let i = 1; i <= crewSize; i++) {
        if (!updated[i]) {
          updated[i] = []; // Initialize empty array for new crews
        }
      }
      // Remove assignments for crews beyond crewSize
      Object.keys(updated).forEach(key => {
        const crewNum = parseInt(key);
        if (crewNum > crewSize) {
          delete updated[crewNum];
        }
      });
      return updated;
    });
  }, [crewSize]);

  // Load scheduled services and saved routes for selected date
  useEffect(() => {
    if (selectedDate) {
      fetchScheduledServices();
      // Always fetch saved routes when date changes or component mounts
      // This ensures saved routes are loaded when navigating back to the page
      // The fetchCompletedRoutes function will preserve existing routes if they exist in memory
      // but will load saved routes from database when component remounts (optimizedRoutes will be empty)
      // Pass true to preserveOptimizedRoutes to prevent clearing routes that were just sorted
      fetchCompletedRoutes(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, providerId]); // Removed fetchCompletedRoutes from deps to prevent unnecessary refetches
  
  // Also fetch routes when existingCrews loads (if routes haven't been loaded yet)
  // This ensures routes are loaded even if existingCrews loads after the initial fetch
  // BUT: Don't fetch if routes were just created (preserve user's work)
  useEffect(() => {
    if (selectedDate && Object.keys(existingCrews).length > 0 && optimizedRoutes.length === 0) {
      console.log('[FETCH-ROUTES] Fetching completed routes because optimizedRoutes is empty');
      fetchCompletedRoutes(true); // Pass true to preserveOptimizedRoutes to be safe
    } else if (optimizedRoutes.length > 0) {
      console.log('[FETCH-ROUTES] Skipping fetch - routes already exist:', optimizedRoutes.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingCrews]); // Run when existingCrews changes (loads)

  const fetchScheduledServices = async () => {
    if (!selectedDate) return;
    
    try {
      setLoadingServices(true);
      // Get local date components to match what the user selected
      // The user selects a date in Central Time (CST/CDT)
      const selectedYear = selectedDate.getFullYear();
      const selectedMonth = selectedDate.getMonth();
      const selectedDay = selectedDate.getDate();
      
      // Create date range for the selected date in Central Time
      // Start of day in Central Time
      const startOfDayCST = new Date(selectedYear, selectedMonth, selectedDay, 0, 0, 0, 0);
      // End of day in Central Time
      const endOfDayCST = new Date(selectedYear, selectedMonth, selectedDay, 23, 59, 59, 999);
      
      // Convert Central Time to UTC for API call
      // CST is UTC-6, CDT is UTC-5 (JavaScript handles DST automatically)
      // getTimezoneOffset() returns offset in minutes (positive means behind UTC)
      // For Central Time: CST = +360 minutes (UTC-6), CDT = +300 minutes (UTC-5)
      const offsetMinutes = startOfDayCST.getTimezoneOffset();
      const offsetMs = offsetMinutes * 60 * 1000;
      
      // Convert to UTC by subtracting the offset
      const startOfDay = new Date(startOfDayCST.getTime() - offsetMs);
      const endOfDay = new Date(endOfDayCST.getTime() - offsetMs);

      console.log('[FETCH-SCHEDULED-SERVICES] Fetching services for date:', {
        selectedDate: `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`,
        startOfDay: startOfDay.toISOString(),
        endOfDay: endOfDay.toISOString()
      });

      // SINGLE SOURCE OF TRUTH: Fetch both scheduled services AND full customer data (same as Customer Details)
      const [scheduleResponse, customersResponse] = await Promise.all([
        apiService.get(
          `/service-requests/provider/schedule?startDate=${startOfDay.toISOString()}&endDate=${endOfDay.toISOString()}`
        ),
        apiService.get('/route-optimization/existing-customers') // Same endpoint as Customer Details
      ]);

      console.log('[FETCH-SCHEDULED-SERVICES] Response:', {
        success: scheduleResponse.success,
        serviceCount: Array.isArray(scheduleResponse.data) ? scheduleResponse.data.length : 0,
        services: Array.isArray(scheduleResponse.data) ? scheduleResponse.data.map((s: any) => ({
          id: s._id?.toString(),
          customerName: s.customerId?.firstName && s.customerId?.lastName ? `${s.customerId.firstName} ${s.customerId.lastName}` : 'Unknown',
          serviceType: s.serviceType?.category || s.serviceType?.name,
          status: s.status,
          scheduledDate: s.scheduledDate
        })) : []
      });

      // Build map of customerId -> Customer data (complete map from both sources)
      // CRITICAL: Include customers from BOTH:
      // 1. ExistingCustomer collection (onboarded customers)
      // 2. ServiceRequest.customerId (bid-accepted customers who aren't onboarded yet)
      // This ensures ALL customers in scheduled services are in the map
      const customersMap = new Map<string, any>();
      
      // First, add all customers from ExistingCustomer collection
      if (customersResponse.success && Array.isArray(customersResponse.data)) {
        customersResponse.data.forEach((customer: any) => {
          const customerId = customer.id || customer._id;
          const linkedCustomerId = customer.linkedCustomerId?._id || customer.linkedCustomerId;
          // Map by ExistingCustomer._id (for direct lookup)
          if (customerId) {
            customersMap.set(customerId.toString(), customer);
          }
          // Map by linkedCustomerId (for onboarded customers where serviceRequest.customerId = linkedCustomerId)
          if (linkedCustomerId) {
            customersMap.set(linkedCustomerId.toString(), customer);
          }
        });
        console.log('[FETCH-SCHEDULED-SERVICES] Loaded', customersResponse.data.length, 'existing customers into map');
      }

      // Second, add customers from scheduled services who aren't in the map yet
      // This handles bid-accepted customers who haven't been onboarded
      if (scheduleResponse.success && scheduleResponse.data && Array.isArray(scheduleResponse.data)) {
        const services = scheduleResponse.data;
        let addedFromServices = 0;
        
        services.forEach((service: any) => {
          const serviceCustomerId = (service.customerId as any)?._id || (service.customerId as any)?.id || service.customerId;
          const customerIdStr = serviceCustomerId ? serviceCustomerId.toString() : null;
          
          // Only add if not already in map (don't overwrite ExistingCustomer data)
          if (customerIdStr && !customersMap.has(customerIdStr) && service.customerId) {
            // Create a minimal customer object from service data
            // This allows getServiceFrequency to work even for bid-accepted customers
            const customerFromService = {
              id: customerIdStr,
              _id: customerIdStr,
              firstName: service.customerId.firstName || '',
              lastName: service.customerId.lastName || '',
              email: service.customerId.email || '',
              phone: service.customerId.phone || '',
              // Use servicePricing from service.customerId if available (from backend)
              servicePricing: service.customerId.servicePricing || null,
              // Mark as bid-accepted customer (not onboarded)
              customerSource: 'bid',
              // No linkedCustomerId - this is the actual customer ID
            };
            customersMap.set(customerIdStr, customerFromService);
            addedFromServices++;
          }
        });
        
        console.log('[FETCH-SCHEDULED-SERVICES] Added', addedFromServices, 'customers from services (bid-accepted, not onboarded)');
        console.log('[FETCH-SCHEDULED-SERVICES] Total customers in map:', customersMap.size, 'entries');
        
        setScheduledServices(services);
        console.log('[FETCH-SCHEDULED-SERVICES] Set scheduled services:', services.length);
        // Clear any previous errors if services are found
        if (services.length > 0) {
          setError(null);
        } else {
          console.warn('[FETCH-SCHEDULED-SERVICES] No services found for selected date');
        }
      }
      
      // Set the complete customer map (includes both existing customers and service customers)
      setExistingCustomersMap(customersMap);
    } catch (err: any) {
      console.error('[FETCH-SCHEDULED-SERVICES] Failed to fetch scheduled services:', err);
      setError('Failed to load scheduled services');
    } finally {
      setLoadingServices(false);
    }
  };

  // Helper to get crew name from existing crews or default
  const getCrewName = (crewNumber: number): string => {
    const existing = existingCrews[crewNumber];
    return existing ? existing.crewName : `Crew ${crewNumber}`;
  };

  // K-means clustering function for geospatial assignment
  const performKMeansClustering = (stops: any[], k: number): any[] => {
    if (stops.length === 0 || k === 0) return [];
    if (k === 1) return [{ crewName: getCrewName(1), customerIds: stops.map(s => s.id) }];
    
    // Filter out stops without valid coordinates and use fallback for those
    // A coordinate is valid if it's a number, not NaN, and not (0,0) which indicates missing data
    const stopsWithCoords = stops.filter(s => 
      s.coordinates && 
      typeof s.coordinates.lat === 'number' && 
      typeof s.coordinates.lng === 'number' &&
      !isNaN(s.coordinates.lat) && 
      !isNaN(s.coordinates.lng) &&
      !(s.coordinates.lat === 0 && s.coordinates.lng === 0) // (0,0) means missing coordinates
    );
    const stopsWithoutCoords = stops.filter(s => 
      !s.coordinates || 
      typeof s.coordinates.lat !== 'number' || 
      typeof s.coordinates.lng !== 'number' ||
      isNaN(s.coordinates.lat) || 
      isNaN(s.coordinates.lng) ||
      s.coordinates.lat === 0 || 
      s.coordinates.lng === 0
    );
    
    // If no stops have valid coordinates, return even distribution
    if (stopsWithCoords.length === 0) {
      const stopsPerCrew = Math.ceil(stops.length / k);
      const assignments = [];
      for (let crewIndex = 0; crewIndex < k; crewIndex++) {
        const startIndex = crewIndex * stopsPerCrew;
        const endIndex = Math.min(startIndex + stopsPerCrew, stops.length);
        const crewStops = stops.slice(startIndex, endIndex);
        if (crewStops.length > 0) {
          assignments.push({
            crewName: getCrewName(crewIndex + 1),
            customerIds: crewStops.map(stop => stop.id)
          });
        }
      }
      return assignments;
    }
    
    // Special case: If only 1 stop has valid coordinates, assign it to crew 1 and distribute others evenly
    if (stopsWithCoords.length === 1 && k > 1) {
      const validStop = stopsWithCoords[0];
      const otherStops = stopsWithoutCoords;
      const assignments = [];
      
      // Crew 1 gets the stop with valid coordinates
      assignments.push({
        crewName: getCrewName(1),
        customerIds: [validStop.id]
      });
      
      // Distribute remaining stops evenly among remaining crews
      const remainingCrews = k - 1;
      const stopsPerCrew = Math.ceil(otherStops.length / remainingCrews);
      for (let crewIndex = 0; crewIndex < remainingCrews; crewIndex++) {
        const startIndex = crewIndex * stopsPerCrew;
        const endIndex = Math.min(startIndex + stopsPerCrew, otherStops.length);
        const crewStops = otherStops.slice(startIndex, endIndex);
        if (crewStops.length > 0) {
          assignments.push({
            crewName: getCrewName(crewIndex + 2),
            customerIds: crewStops.map(stop => stop.id)
          });
        }
      }
      
      console.log('[CLUSTERING] Only 1 stop with valid coordinates - using special assignment:', assignments);
      return assignments;
    }
    
    // Use effective k (min of k and number of stops with coords)
    const effectiveK = Math.min(k, stopsWithCoords.length);
    
    // Initialize centroids using farthest-first approach for better geographic separation
    // This ensures distant stops are assigned to different crews
    let centroids: Array<{ lat: number; lng: number }> = [];
    
    if (effectiveK === 1) {
      // Single crew - use first stop as centroid
      const firstStop = stopsWithCoords.find(s => 
        s.coordinates && 
        typeof s.coordinates.lat === 'number' && 
        typeof s.coordinates.lng === 'number'
      );
      if (firstStop && firstStop.coordinates) {
        centroids.push({ lat: firstStop.coordinates.lat, lng: firstStop.coordinates.lng });
      }
    } else {
      // Multiple crews - use farthest-first initialization
      // Step 1: Find the two stops that are farthest apart
      // Only use stops with valid, non-zero coordinates
      const validStops = stopsWithCoords.filter(s => 
        s.coordinates && 
        typeof s.coordinates.lat === 'number' && 
        typeof s.coordinates.lng === 'number' &&
        !isNaN(s.coordinates.lat) && 
        !isNaN(s.coordinates.lng) &&
        s.coordinates.lat !== 0 && 
        s.coordinates.lng !== 0
      );
      
      let maxDistance = 0;
      let farthestPair: [any, any] | null = null;
      
      for (let i = 0; i < validStops.length; i++) {
        const stop1 = validStops[i];
        
        for (let j = i + 1; j < validStops.length; j++) {
          const stop2 = validStops[j];
          
          const dist = calculateDistance(stop1.coordinates, stop2.coordinates);
          if (dist > maxDistance) {
            maxDistance = dist;
            farthestPair = [stop1, stop2];
          }
        }
      }
      
      // Step 2: Use the farthest pair as initial centroids (for 2 crews)
      if (farthestPair && farthestPair[0].coordinates && farthestPair[1].coordinates) {
        centroids.push({ 
          lat: farthestPair[0].coordinates.lat, 
          lng: farthestPair[0].coordinates.lng 
        });
        centroids.push({ 
          lat: farthestPair[1].coordinates.lat, 
          lng: farthestPair[1].coordinates.lng 
        });
      }
      
      // Step 3: For more than 2 crews, find additional centroids that are farthest from existing ones
      while (centroids.length < effectiveK && centroids.length < stopsWithCoords.length) {
        let maxMinDistance = 0;
        let bestNextStop: any | null = null;
        
        for (const stop of stopsWithCoords) {
          if (!stop.coordinates || typeof stop.coordinates.lat !== 'number' || typeof stop.coordinates.lng !== 'number') {
            continue;
          }
          
          // Skip if this stop is already a centroid
          if (centroids.some(c => 
            Math.abs(c.lat - stop.coordinates.lat) < 0.0001 && 
            Math.abs(c.lng - stop.coordinates.lng) < 0.0001
          )) {
            continue;
          }
          
          // Find minimum distance to any existing centroid
          let minDistanceToCentroids = Infinity;
          for (const centroid of centroids) {
            const dist = calculateDistance(stop.coordinates, centroid);
            if (dist < minDistanceToCentroids) {
              minDistanceToCentroids = dist;
            }
          }
          
          // Choose the stop that is farthest from all existing centroids
          if (minDistanceToCentroids > maxMinDistance) {
            maxMinDistance = minDistanceToCentroids;
            bestNextStop = stop;
          }
        }
        
        if (bestNextStop && bestNextStop.coordinates) {
          centroids.push({ 
            lat: bestNextStop.coordinates.lat, 
            lng: bestNextStop.coordinates.lng 
          });
        } else {
          // Fallback: use any remaining stop
          const remainingStop = stopsWithCoords.find(s => 
            s.coordinates && 
            typeof s.coordinates.lat === 'number' && 
            typeof s.coordinates.lng === 'number' &&
            !centroids.some(c => 
              Math.abs(c.lat - s.coordinates.lat) < 0.0001 && 
              Math.abs(c.lng - s.coordinates.lng) < 0.0001
            )
          );
          if (remainingStop && remainingStop.coordinates) {
            centroids.push({ 
              lat: remainingStop.coordinates.lat, 
              lng: remainingStop.coordinates.lng 
            });
          } else {
            break;
          }
        }
      }
    }
    
    console.log('[CLUSTERING] Initialized centroids using farthest-first:', {
      requestedCrews: k,
      effectiveK,
      centroids: centroids.length,
      centroidLocations: centroids
    });
    
    // Run k-means iterations
    for (let iteration = 0; iteration < 20; iteration++) {
      // Assign stops to nearest centroid
      const clusters: any[][] = Array(effectiveK).fill(null).map(() => []);
      
      for (const stop of stopsWithCoords) {
        if (!stop.coordinates || typeof stop.coordinates.lat !== 'number' || typeof stop.coordinates.lng !== 'number') {
          continue;
        }
        let minDist = Infinity;
        let nearestCluster = 0;
        
        for (let i = 0; i < effectiveK; i++) {
          const dist = calculateDistance(stop.coordinates, centroids[i]);
          if (dist < minDist) {
            minDist = dist;
            nearestCluster = i;
          }
        }
        clusters[nearestCluster].push(stop);
      }
      
      // Recalculate centroids
      let changed = false;
      for (let i = 0; i < effectiveK; i++) {
        if (clusters[i].length === 0) {
          // Empty cluster - assign a random stop with valid coordinates
          const randomStop = stopsWithCoords.find(s => 
            s.coordinates && 
            typeof s.coordinates.lat === 'number' && 
            typeof s.coordinates.lng === 'number'
          );
          if (randomStop && randomStop.coordinates) {
            centroids[i] = { lat: randomStop.coordinates.lat, lng: randomStop.coordinates.lng };
          }
          changed = true;
        } else {
          const validStops = clusters[i].filter(s => 
            s.coordinates && 
            typeof s.coordinates.lat === 'number' && 
            typeof s.coordinates.lng === 'number'
          );
          if (validStops.length > 0) {
            const newCentroid = {
              lat: validStops.reduce((sum, s) => sum + (s.coordinates?.lat || 0), 0) / validStops.length,
              lng: validStops.reduce((sum, s) => sum + (s.coordinates?.lng || 0), 0) / validStops.length,
            };
            if (Math.abs(newCentroid.lat - centroids[i].lat) > 0.0001 || 
                Math.abs(newCentroid.lng - centroids[i].lng) > 0.0001) {
              changed = true;
            }
            centroids[i] = newCentroid;
          }
        }
      }
      
      // If no centroids changed, we've converged
      if (!changed) break;
    }
    
    // Final assignment
    const clusters: any[][] = Array(effectiveK).fill(null).map(() => []);
    for (const stop of stopsWithCoords) {
      if (!stop.coordinates || typeof stop.coordinates.lat !== 'number' || typeof stop.coordinates.lng !== 'number') {
        continue;
      }
      let minDist = Infinity;
      let nearestCluster = 0;
      
      for (let i = 0; i < effectiveK; i++) {
        const dist = calculateDistance(stop.coordinates, centroids[i]);
        if (dist < minDist) {
          minDist = dist;
          nearestCluster = i;
        }
      }
      clusters[nearestCluster].push(stop);
    }
    
    // Distribute stops without coordinates evenly
    let coordIndex = 0;
    for (const stop of stopsWithoutCoords) {
      clusters[coordIndex % effectiveK].push(stop);
      coordIndex++;
    }
    
    // Ensure we always have exactly k crews (or as many as possible)
    // If we have fewer clusters than requested crews, split the largest clusters
    const nonEmptyClusters = clusters.filter(c => c.length > 0);
    
    console.log('[CLUSTERING] Final clusters:', {
      requestedCrews: k,
      effectiveK,
      nonEmptyClusters: nonEmptyClusters.length,
      clusterSizes: nonEmptyClusters.map(c => c.length)
    });
    
    // If we have fewer clusters than requested, we need to split some clusters
    if (nonEmptyClusters.length < k && nonEmptyClusters.length > 0) {
      console.log('[CLUSTERING] Splitting clusters to create', k, 'crews from', nonEmptyClusters.length, 'clusters');
      // Sort clusters by size (largest first) so we split the biggest ones
      const sortedClusters = [...nonEmptyClusters].sort((a, b) => b.length - a.length);
      const finalClusters: any[][] = [];
      
      // Keep the clusters that are already separate (up to k)
      for (let i = 0; i < Math.min(nonEmptyClusters.length, k); i++) {
        finalClusters.push([...sortedClusters[i]]);
      }
      
      // If we still need more crews, split the largest cluster(s)
      let splitIndex = 0;
      while (finalClusters.length < k && splitIndex < sortedClusters.length) {
        // Find the largest cluster that can be split
        const largestCluster = sortedClusters[splitIndex];
        if (largestCluster.length <= 1) {
          // Can't split this one, try the next
          splitIndex++;
          continue;
        }
        
        // Split the largest cluster in half
        const midPoint = Math.ceil(largestCluster.length / 2);
        const firstHalf = largestCluster.slice(0, midPoint);
        const secondHalf = largestCluster.slice(midPoint);
        
        // Replace the cluster in finalClusters with the first half
        const clusterIndex = finalClusters.findIndex(c => c === largestCluster || 
          (c.length === largestCluster.length && c[0]?.id === largestCluster[0]?.id));
        if (clusterIndex >= 0) {
          finalClusters[clusterIndex] = firstHalf;
        }
        
        // Add the second half as a new cluster
        finalClusters.push(secondHalf);
        splitIndex++;
      }
      
      console.log('[CLUSTERING] After splitting:', {
        finalCrews: finalClusters.length,
        crewSizes: finalClusters.map(c => c.length)
      });
      
      // Convert to assignment format
      return finalClusters
        .filter(c => c.length > 0)
        .map((cluster, index) => ({
          crewName: getCrewName(index + 1),
          customerIds: cluster.map((s: any) => s.id)
        }));
    }
    
    // Convert clusters to assignment format
    return nonEmptyClusters
      .map((cluster, index) => ({
        crewName: getCrewName(index + 1),
        customerIds: cluster.map((s: any) => s.id)
      }));
  };

  // Calculate distance between two coordinates (Haversine formula)
  const calculateDistance = (coord1: { lat: number; lng: number } | null | undefined, coord2: { lat: number; lng: number } | null | undefined): number => {
    // Return 0 if either coordinate is invalid or is (0,0) which indicates missing coordinates
    if (!coord1 || !coord2 || 
        typeof coord1.lat !== 'number' || typeof coord1.lng !== 'number' ||
        typeof coord2.lat !== 'number' || typeof coord2.lng !== 'number' ||
        isNaN(coord1.lat) || isNaN(coord1.lng) || isNaN(coord2.lat) || isNaN(coord2.lng) ||
        (coord1.lat === 0 && coord1.lng === 0) || (coord2.lat === 0 && coord2.lng === 0)) {
      return 0;
    }
    
    const R = 3959; // Earth radius in miles
    const lat1 = coord1.lat * Math.PI / 180;
    const lat2 = coord2.lat * Math.PI / 180;
    const deltaLat = (coord2.lat - coord1.lat) * Math.PI / 180;
    const deltaLng = (coord2.lng - coord1.lng) * Math.PI / 180;
    
    const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
              Math.cos(lat1) * Math.cos(lat2) *
              Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    
    return R * c;
  };

  const handleOptimizeRoutes = async () => {
    console.log('🚀🚀🚀 handleOptimizeRoutes CALLED');
    if (!selectedDate || scheduledServices.length === 0) {
      setError('No services found for the selected date. Please schedule some services first.');
      return;
    }

    try {
      setOptimizing(true);
      setError(null);

      // Format date as YYYY-MM-DD in local timezone (not UTC)
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;

      // Check if routes already exist (from Sort Services)
      const hasExistingRoutes = optimizedRoutes.length > 0 && !optimizedRoutes.every(route => route.jobs.length === 0);

      if (hasExistingRoutes) {
        // Re-optimize existing routes (routes were already sorted/assigned)
        console.log('🚀🚀🚀 Re-optimizing existing routes...');

      // Convert existing routes to crew assignments format for optimization
      const crewAssignments = optimizedRoutes.map(route => ({
        crewName: route.crewName,
        customerIds: route.jobs.map(job => {
          // Extract service ID from job - handle both string and object IDs
          const serviceId = job.customerId?.toString() || String(job.customerId || '');
          return serviceId;
        }).filter(Boolean)
      })).filter(assignment => assignment.customerIds.length > 0);

      if (crewAssignments.length === 0) {
        setError('No routes with services found. Please click "Sort and Optimize" first.');
        setOptimizing(false);
        return;
      }

      console.log('🚀🚀🚀 Making API call to /route-optimization/assign-crews with', crewAssignments.length, 'crews');
      console.log('🚀🚀🚀 Crew assignments:', JSON.stringify(crewAssignments, null, 2));
      
      const response = await apiService.post('/route-optimization/assign-crews', {
        date: dateStr,
        crewAssignments: crewAssignments,
        useGoogleOptimization: false // Temporarily disabled - Google API is returning errors. Using our algorithm instead.
      });

      console.log('🚀🚀🚀 API RESPONSE RECEIVED:', JSON.stringify(response, null, 2));

      if (response.success) {
        const data = response.data as any;
        const crews = data?.crews || [];
        
        if (crews.length > 0) {
          setOptimizedRoutes(crews);
            setSuccess(`Routes re-optimized successfully! Optimized ${crews.length} crew routes.`);
        } else {
          setError('No routes were generated. Please try again.');
        }
      } else {
        setError(response.error || 'Failed to optimize routes');
        }
      } else {
        // No routes exist yet - assign and optimize in one step (especially useful for single crew)
        console.log('🚀🚀🚀 Assigning and optimizing routes in one step (crewSize:', crewSize, ')');
        
        const response = await apiService.post('/route-optimization/optimize', {
          date: dateStr,
          crewSize: crewSize,
          useGoogleOptimization: false // Temporarily disabled - Google API is returning errors. Using our algorithm instead.
        });

        console.log('🚀🚀🚀 API RESPONSE RECEIVED:', JSON.stringify(response, null, 2));

        if (response.success) {
          const data = response.data as any;
          const crews = data?.crews || [];
          
          if (crews.length > 0) {
            setOptimizedRoutes(crews);
            setSuccess(`Routes assigned and optimized successfully! Created ${crews.length} crew route${crews.length !== 1 ? 's' : ''}.`);
          } else {
            setError('No routes were generated. Please try again.');
          }
        } else {
          setError(response.error || 'Failed to optimize routes');
        }
      }
    } catch (err: any) {
      console.log('🚀🚀🚀 ERROR in handleOptimizeRoutes:', err);
      setError(err.message || 'Failed to optimize routes');
    } finally {
      console.log('🚀🚀🚀 Setting optimizing to false');
      setOptimizing(false);
    }
  };

  // Sort services by crew assignments
  const handleSortServices = async () => {
    if (!selectedDate || scheduledServices.length === 0) {
      setError('No services found for the selected date.');
      return;
    }

    // Filter out services for customers on payment hold and completed services
    const availableServices = scheduledServices.filter(service => 
      !service.isOnPaymentHold && service.status !== 'completed'
    );

    // Get service types that are actually scheduled (present in availableServices)
    const scheduledServiceTypes = new Set<string>();
    availableServices.forEach(service => {
      const serviceCategory = service.serviceType?.category;
      if (serviceCategory) {
        scheduledServiceTypes.add(serviceCategory);
      }
    });
    
    // Only check crew assignments for service types that are actually scheduled
    const serviceTypeNames: Record<string, string> = {
      mowing: 'Lawn Mowing',
      fertilizing: 'Weed Control/Fertilizing',
      snow_removal: 'Snow Removal',
      additional_services: 'Additional Services',
    };
    
    const unassignedServices: string[] = [];
    scheduledServiceTypes.forEach(serviceType => {
      const hasAssignment = Object.values(crewServiceAssignments).some(types => 
        types.includes(serviceType)
      );
      if (!hasAssignment) {
        unassignedServices.push(serviceTypeNames[serviceType] || serviceType);
      }
    });

    if (unassignedServices.length > 0) {
      setError(
        `The following service types are not assigned to any crew: ${unassignedServices.join(', ')}. ` +
          'Open Crew Management → Crews → Assign Services, then try again.'
      );
      return;
    }

    try {
      setOptimizing(true);
      setError(null);
      setSuccess(null);

      // availableServices was already filtered above before the crew assignment check
      // Group services by service type
      const servicesByType: Record<string, ScheduledService[]> = {
        mowing: [],
        fertilizing: [],
        snow_removal: [],
        additional_services: [],
      };

      availableServices.forEach(service => {
        const serviceCategory = service.serviceType?.category;
        if (serviceCategory && servicesByType[serviceCategory]) {
          servicesByType[serviceCategory].push(service);
        }
      });

      // For each service type, find assigned crews and cluster geographically
      const servicesByCrew: Record<number, ScheduledService[]> = {};
      
      // Initialize arrays for each crew
      for (let i = 1; i <= crewSize; i++) {
        servicesByCrew[i] = [];
      }

      // Process each service type
      Object.keys(servicesByType).forEach(serviceCategory => {
        const services = servicesByType[serviceCategory];
        if (services.length === 0) return;

        // Find which crews are assigned to this service type
        const assignedCrews = Object.keys(crewServiceAssignments)
          .map(crewNumStr => parseInt(crewNumStr))
          .filter(crewNum => crewServiceAssignments[crewNum].includes(serviceCategory))
          .sort((a, b) => a - b);

        if (assignedCrews.length === 0) return;

        // Convert services to route stops
        const routeStops = services.map(service => ({
          id: service._id?.toString() || String(service._id || ''), // Ensure ID is always a string
          name: `${service.customerId.firstName} ${service.customerId.lastName}`,
          address: `${service.address.street}, ${service.address.city}, ${service.address.state} ${service.address.zipCode}`,
          coordinates: service.address.coordinates || { lat: 0, lng: 0 },
          estimatedDuration: service.estimatedDuration || 60,
          preferredTime: service.scheduledTime || '09:00',
          notes: service.notes || (service as any).specialInstructions,
          service: service, // Keep reference to original service
        }));

        // Cluster stops geographically based on number of assigned crews
        const clusters = performKMeansClustering(routeStops, assignedCrews.length);

        // Assign each cluster to a different crew
        clusters.forEach((cluster, clusterIndex) => {
          const crewNum = assignedCrews[clusterIndex % assignedCrews.length];
          
          console.log(`[SORT-SERVICES] Processing cluster ${clusterIndex} for crew ${crewNum}:`, {
            clusterCustomerIds: cluster.customerIds,
            servicesCount: services.length,
            serviceIds: services.map(s => s._id?.toString() || s._id)
          });
          
          // Add services from this cluster to the crew's list
          // CRITICAL: Track assigned services to prevent duplicates
          cluster.customerIds.forEach((serviceId: string) => {
            // Normalize IDs for comparison (handle both string and object IDs)
            const normalizedServiceId = String(serviceId).trim();
            
            // Check if this service was already assigned to another crew
            let alreadyAssigned = false;
            let assignedToCrew: number | null = null;
            for (let checkCrewNum = 1; checkCrewNum <= crewSize; checkCrewNum++) {
              if (checkCrewNum !== crewNum && servicesByCrew[checkCrewNum]) {
                const alreadyInCrew = servicesByCrew[checkCrewNum].some(s => {
                  const serviceIdStr = s._id?.toString() || String(s._id || '').trim();
                  return serviceIdStr === normalizedServiceId;
                });
                if (alreadyInCrew) {
                  alreadyAssigned = true;
                  assignedToCrew = checkCrewNum;
                  break;
                }
              }
            }
            
            if (alreadyAssigned) {
              console.warn(`[SORT-SERVICES] ⚠️ Skipping duplicate service ${serviceId} - already assigned to crew ${assignedToCrew}`, {
                serviceId: normalizedServiceId,
                skippedForCrew: crewNum,
                alreadyInCrew: assignedToCrew
              });
              return; // Skip this service - already assigned
            }
            
            const service = services.find(s => {
              const serviceIdStr = s._id?.toString() || String(s._id || '').trim();
              return serviceIdStr === normalizedServiceId;
            });
            
            if (service) {
              if (!servicesByCrew[crewNum]) {
                servicesByCrew[crewNum] = [];
              }
              servicesByCrew[crewNum].push(service);
              console.log(`[SORT-SERVICES] ✓ Assigned service ${serviceId} to crew ${crewNum}`);
            } else {
              console.warn(`[SORT-SERVICES] ✗ Could not find service with ID: ${serviceId}`, {
                lookingFor: normalizedServiceId,
                availableIds: services.map(s => s._id?.toString() || s._id)
              });
            }
          });
        });
      });

      // Convert to route stops and cluster for each crew (for optimal ordering)
      const crewRoutes: OptimizedRoute[] = [];
      
      console.log('[SORT-SERVICES] Services by crew summary:', {
        crewSize,
        servicesByCrew: Object.keys(servicesByCrew).map(crewNum => ({
          crewNum: parseInt(crewNum),
          serviceCount: servicesByCrew[parseInt(crewNum)]?.length || 0
        }))
      });
      
      for (let crewNum = 1; crewNum <= crewSize; crewNum++) {
        const crewServices = servicesByCrew[crewNum] || [];
        console.log(`[SORT-SERVICES] Processing crew ${crewNum}:`, {
          serviceCount: crewServices.length,
          serviceIds: crewServices.map(s => s._id?.toString() || s._id)
        });
        
        if (crewServices.length === 0) {
          console.log(`[SORT-SERVICES] Skipping crew ${crewNum} - no services assigned`);
          continue;
        }

        const routeStops = crewServices.map(service => ({
          id: service._id?.toString() || String(service._id || ''), // Ensure ID is always a string
          name: `${service.customerId.firstName} ${service.customerId.lastName}`,
          address: `${service.address.street}, ${service.address.city}, ${service.address.state} ${service.address.zipCode}`,
          coordinates: service.address.coordinates || { lat: 0, lng: 0 },
          estimatedDuration: service.estimatedDuration || 60,
          preferredTime: service.scheduledTime || '09:00',
          notes: service.notes || (service as any).specialInstructions,
        }));

        // Cluster stops for this crew (single crew, but still cluster for optimal order)
        console.log(`[SORT-SERVICES] Clustering ${routeStops.length} stops for crew ${crewNum}`);
        const clusters = performKMeansClustering(routeStops, 1);
        console.log(`[SORT-SERVICES] Clustering result for crew ${crewNum}:`, {
          clusterCount: clusters.length,
          firstClusterSize: clusters[0]?.customerIds?.length || 0,
          firstClusterIds: clusters[0]?.customerIds || []
        });
        
        if (clusters.length > 0 && clusters[0].customerIds.length > 0) {
          // Create route for this crew
          const crewId = `crew-${crewNum}`;
          const crewName = getCrewName(crewNum);
          
          // Get jobs in order
          const jobs = clusters[0].customerIds.map((serviceId: string, idx: number) => {
            // Normalize IDs for comparison (handle both string and object IDs)
            const normalizedServiceId = String(serviceId).trim();
            const service = crewServices.find(s => {
              const serviceIdStr = s._id?.toString() || String(s._id || '').trim();
              return serviceIdStr === normalizedServiceId;
            });
            if (!service) {
              console.warn(`[SORT-SERVICES] ✗ Could not find service with ID: ${serviceId} in crew ${crewNum}`, {
                lookingFor: normalizedServiceId,
                availableIds: crewServices.map(s => s._id?.toString() || s._id)
              });
              return null;
            }
            
            const jobCustomerId = service._id?.toString() || String(service._id || '');
            console.log(`[SORT-SERVICES] Creating job for crew ${crewNum}:`, {
              serviceId: service._id,
              jobCustomerId,
              customerName: `${service.customerId.firstName} ${service.customerId.lastName}`
            });
            
            return {
              customerId: jobCustomerId, // Ensure it's a string for consistent matching
              customerName: `${service.customerId.firstName} ${service.customerId.lastName}`,
              address: `${service.address.street}, ${service.address.city}, ${service.address.state} ${service.address.zipCode}`,
              services: toServiceFlags(service.serviceType),
              estimatedDuration: service.estimatedDuration || 60,
              estimatedArrival: '',
              estimatedDeparture: '',
              coordinates: service.address.coordinates || { lat: 0, lng: 0 },
              preferredTime: service.scheduledTime,
              notes: service.notes || (service as any).specialInstructions,
              sequence: idx + 1,
            };
          }).filter(Boolean);

          crewRoutes.push({
            crewId,
            crewName,
            crewSize: 1,
            jobs: jobs as any,
            totalTravelTime: 0,
            totalServiceTime: jobs.reduce((sum: number, j: any) => sum + (j?.estimatedDuration || 60), 0),
            totalDistance: 0,
          });
        }
      }

      // Sort assigns services to crews, then optimize stop order within each crew
      console.log('[SORT-AND-OPTIMIZE] Sorted routes:', {
        routeCount: crewRoutes.length,
        routes: crewRoutes.map(r => ({
          crewId: r.crewId,
          crewName: r.crewName,
          jobCount: r.jobs.length,
          jobIds: r.jobs.map(j => j.customerId?.toString() || j.customerId)
        }))
      });

      setOptimizedRoutes(crewRoutes);

      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;

      const crewAssignments = crewRoutes
        .map((route) => ({
          crewName: route.crewName,
          customerIds: route.jobs
            .map((job) => job.customerId?.toString() || String(job.customerId || ''))
            .filter(Boolean),
        }))
        .filter((a) => a.customerIds.length > 0);

      if (crewAssignments.length === 0) {
        setSuccess(
          `Services sorted to ${crewRoutes.length} crew${crewRoutes.length !== 1 ? 's' : ''}, but no jobs to optimize.`
        );
        return;
      }

      const optimizeResponse = await apiService.post('/route-optimization/assign-crews', {
        date: dateStr,
        crewAssignments,
        useGoogleOptimization: false,
      });

      if (optimizeResponse.success) {
        const data = optimizeResponse.data as any;
        const crews = data?.crews || [];
        if (crews.length > 0) {
          setOptimizedRoutes(crews);
          setSuccess(
            `Sorted and optimized ${crews.length} crew route${crews.length !== 1 ? 's' : ''}. ` +
              'You can move stops between crews, then click Re-optimize to reorder without re-sorting.'
          );
        } else {
          setSuccess(
            `Services sorted to crews. Optimization returned no routes — try Re-optimize.`
          );
        }
      } else {
        setSuccess(
          `Services sorted to crews. Optimization failed (${optimizeResponse.error || 'unknown'}). Try Re-optimize.`
        );
      }
    } catch (err: any) {
      setError(err.message || 'Failed to sort and optimize services');
    } finally {
      setOptimizing(false);
    }
  };

  const handleRescheduleService = (service: ScheduledService) => {
    // Create a fresh copy of the service to avoid reference issues
    const serviceCopy = {
      ...service,
      customerId: { ...service.customerId },
      address: { ...service.address }
    };
    
    console.log('[RESCHEDULE] Opening dialog for service:', {
      serviceId: service._id,
      customerId: service.customerId._id,
      customerName: `${service.customerId.firstName} ${service.customerId.lastName}`
    });
    
    setRescheduleDialog({
      open: true,
      service: serviceCopy,
      newDate: service.scheduledDate ? service.scheduledDate.split('T')[0] : '',
      newTime: service.scheduledTime || '',
      changeType: 'one-time'
    });
  };

  const submitReschedule = async () => {
    if (!rescheduleDialog.service) return;

    try {
      const response = await apiService.put(`/schedule/${rescheduleDialog.service._id}`, {
        scheduledDate: rescheduleDialog.newDate,
        scheduledTime: rescheduleDialog.newTime,
        notes: rescheduleDialog.service.notes,
        resetRecurringSchedule: rescheduleDialog.changeType === 'permanent'
      });

      if (response.success) {
        const changeTypeText = rescheduleDialog.changeType === 'permanent' ? 'permanently' : 'as one-time';
        setSuccess(`Service rescheduled successfully (${changeTypeText})`);
        setRescheduleDialog({ open: false, service: null, newDate: '', newTime: '', changeType: 'one-time' });
        fetchScheduledServices(); // Refresh the list
      } else {
        setError(response.error || 'Failed to reschedule service');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to reschedule service');
    }
  };

  const handleAssignToRoute = async () => {
    if (!assignToRouteDialog.service || !assignToRouteDialog.selectedRouteId) return;

    try {
      const selectedRoute = optimizedRoutes.find(r => r.crewId === assignToRouteDialog.selectedRouteId);
      if (!selectedRoute) {
        setError('Selected route not found');
        return;
      }

      // Create job data for the service
      const service = assignToRouteDialog.service;
      // IMPORTANT: customerId must be the ServiceRequest _id, not the customer User ID
      // The crew endpoint looks up ServiceRequests by _id using job.customerId
      
      // Convert serviceType to services boolean flags format expected by database
      const servicesFlags = toServiceFlags(service.serviceType);
      
      const newJob = {
        customerId: service._id, // ServiceRequest ID, not customer User ID
        customerName: `${service.customerId.firstName} ${service.customerId.lastName}`,
        address: `${service.address.street}, ${service.address.city}, ${service.address.state} ${service.address.zipCode}`,
        services: servicesFlags, // Use boolean flags format, not serviceType object
        estimatedDuration: service.estimatedDuration || 60,
        estimatedArrival: service.scheduledTime || '09:00',
        estimatedDeparture: service.scheduledTime || '09:00',
        coordinates: service.address.coordinates || { lat: 0, lng: 0 },
        preferredTime: service.scheduledTime,
        notes: service.notes,
        sequence: selectedRoute.jobs.length + 1 // Add at the end
      };

      // Update the route by adding the new job
      const updatedRoutes = optimizedRoutes.map(route => {
        if (route.crewId === assignToRouteDialog.selectedRouteId) {
          return {
            ...route,
            jobs: [...route.jobs, newJob],
            totalServiceTime: route.totalServiceTime + (service.estimatedDuration || 60)
          };
        }
        return route;
      });

      setOptimizedRoutes(updatedRoutes);
      
      // Save the updated route to the database so crews see it automatically
      // Format date as YYYY-MM-DD in local timezone
      if (!selectedDate) {
        setError('No date selected. Please select a date first.');
        return;
      }
      
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;

      // Save only the updated route to the database
      const routeToSave = updatedRoutes.find(r => r.crewId === assignToRouteDialog.selectedRouteId);
      if (routeToSave) {
        try {
          console.log('[ASSIGN-TO-ROUTE] Saving updated route to database:', {
            crewId: routeToSave.crewId,
            crewName: routeToSave.crewName,
            jobsCount: routeToSave.jobs.length,
            date: dateStr
          });

          const saveResponse = await apiService.post('/route-optimization/save-routes', {
            date: dateStr,
            routes: [routeToSave] // Save only the updated route
          });

          if (saveResponse.success) {
            setSuccess(`Service added to ${selectedRoute.crewName}. Crew route updated automatically.`);
          } else {
            console.warn('[ASSIGN-TO-ROUTE] Failed to save route to database:', saveResponse.error);
            setSuccess(`Service added to ${selectedRoute.crewName} (route may need to be saved manually).`);
          }
        } catch (saveErr: any) {
          console.error('[ASSIGN-TO-ROUTE] Error saving route to database:', saveErr);
          setSuccess(`Service added to ${selectedRoute.crewName} (route may need to be saved manually).`);
        }
      } else {
        setSuccess(`Service assigned to ${selectedRoute.crewName} successfully`);
      }
      
      setAssignToRouteDialog({ open: false, service: null, selectedRouteId: '' });
    } catch (err: any) {
      setError(err.message || 'Failed to assign service to route');
    }
  };

  const handleMoveJob = (job: any, fromRoute: OptimizedRoute, currentIndex: number) => {
    setMoveJobDialog({
      open: true,
      job,
      fromRoute,
      targetRouteIndex: 0,
      targetPosition: '1'
    });
  };

  const submitMoveJob = () => {
    if (!moveJobDialog.job || !moveJobDialog.fromRoute) return;
    
    const targetRoute = optimizedRoutes[moveJobDialog.targetRouteIndex];
    if (!targetRoute) return;
    
    const position = parseInt(moveJobDialog.targetPosition);
    if (isNaN(position) || position < 1 || position > targetRoute.jobs.length + 1) {
      setError('Invalid position. Please enter a number between 1 and ' + (targetRoute.jobs.length + 1));
      return;
    }
    
    const isSameRoute = moveJobDialog.fromRoute.crewId === targetRoute.crewId;
    
    // If moving within the same route, remove the job first, then add at new position
    // This prevents duplicates and ensures the job appears only at the new position
    let updatedTargetJobs: any[];
    if (isSameRoute) {
      // Remove all instances of this job first
      updatedTargetJobs = targetRoute.jobs.filter(j => j.customerId !== moveJobDialog.job.customerId);
      // Then add at the new position
      updatedTargetJobs.splice(position - 1, 0, moveJobDialog.job);
    } else {
      // Remove from source route
      const updatedSourceJobs = moveJobDialog.fromRoute.jobs.filter(j => j.customerId !== moveJobDialog.job.customerId);
      const updatedSourceRoute = {
        ...moveJobDialog.fromRoute,
        jobs: updatedSourceJobs
      };
      
      // Remove any existing instances from target route (in case of duplicates)
      updatedTargetJobs = targetRoute.jobs.filter(j => j.customerId !== moveJobDialog.job.customerId);
      // Add to target route at specified position
      updatedTargetJobs.splice(position - 1, 0, moveJobDialog.job);
      
      // Update source route
      const sourceRouteIndex = optimizedRoutes.findIndex(r => r.crewId === moveJobDialog.fromRoute!.crewId);
      const updatedRoutes = [...optimizedRoutes];
      updatedRoutes[sourceRouteIndex] = updatedSourceRoute;
      updatedRoutes[moveJobDialog.targetRouteIndex] = {
        ...targetRoute,
        jobs: updatedTargetJobs
      };
      setOptimizedRoutes(updatedRoutes);
      setSuccess(`Moved ${moveJobDialog.job.customerName} to ${targetRoute.crewName} at position ${position}`);
      setMoveJobDialog({ open: false, job: null, fromRoute: null, targetRouteIndex: 0, targetPosition: '1' });
      return;
    }
    
    // Update the same route
    const updatedRoutes = [...optimizedRoutes];
    updatedRoutes[moveJobDialog.targetRouteIndex] = {
      ...targetRoute,
      jobs: updatedTargetJobs
    };
    
    setOptimizedRoutes(updatedRoutes);
    setSuccess(`Moved ${moveJobDialog.job.customerName} to position ${position} in ${targetRoute.crewName}`);
    setMoveJobDialog({ open: false, job: null, fromRoute: null, targetRouteIndex: 0, targetPosition: '1' });
  };

  const handleUpdateCrewName = async (crewId: string, newName: string) => {
    // Update local state immediately for responsive UI
    const updatedRoutes = optimizedRoutes.map(route => {
      if (route.crewId === crewId) {
        return { ...route, crewName: newName };
      }
      return route;
    });
    setOptimizedRoutes(updatedRoutes);
    setEditingCrewName(null);
    
    // Save crew name to database
    try {
      console.log('[ROUTE-OPT] Saving crew name:', { crewId, crewName: newName.trim() });
      const response = await apiService.post(
        `/route-optimization/crew-logins/${crewId}/update-credentials`,
        { crewName: newName.trim() }
      );
      
      if (response.success) {
        // Update existing crews cache
        const crewNumber = parseInt(crewId.replace('crew-', ''), 10);
        const { firstName, lastName } = splitCrewName(newName.trim(), crewNumber);
        setExistingCrews(prev => ({
          ...prev,
          [crewNumber]: {
            firstName,
            lastName,
            crewName: newName.trim()
          }
        }));
        console.log('[ROUTE-OPT] Crew name saved successfully');
      } else {
        console.error('[ROUTE-OPT] Failed to save crew name:', response.error);
        setError(response.error || 'Failed to save crew name. Please try again.');
      }
    } catch (err: any) {
      console.error('[ROUTE-OPT] Error saving crew name:', {
        error: err.message,
        response: err.response?.data,
        status: err.response?.status,
        code: err.code,
        isTimeout: err.code === 'ECONNABORTED' || err.message?.includes('timeout'),
        url: err.config?.url,
        baseURL: err.config?.baseURL,
        fullUrl: err.config?.baseURL ? `${err.config.baseURL}${err.config.url}` : err.config?.url
      });
      
      // Check if it's a timeout error
      // Timeouts can occur even if the server successfully processed the request
      // Since we already updated local state, the UI shows the correct name
      // Only show a warning, not a full error, and don't reset the local state
      const isTimeout = err.code === 'ECONNABORTED' || err.message?.includes('timeout');
      if (isTimeout) {
        // Timeout - server may have processed it, but we can't confirm
        // Since local state was already updated, silently continue or show a gentle notice
        console.warn('[ROUTE-OPT] Request timed out, but crew name was updated locally. The save may have succeeded - please verify.');
        // Don't set error - the local state update already shows the change
        // If the save failed, user will notice on refresh
        return;
      }
      
      // Check if it's a network/CORS error
      if (err.message?.includes('Network Error') || err.message?.includes('CORS') || !err.response) {
        // Network error - but local state was already updated
        // Show a warning instead of error, since the save might have succeeded
        console.warn('[ROUTE-OPT] Network error occurred, but crew name was updated locally. The save may have succeeded - please verify.');
        // Don't set error - the local state update already shows the change
        return;
      } else if (err.response?.status === 403) {
        setError('Access denied. Please refresh the page and try again.');
      } else {
        setError(err.response?.data?.error || err.message || 'Failed to save crew name. Please try again.');
      }
    }
  };
  
  // Helper to split crew name into first and last name
  const splitCrewName = (crewName: string, crewNumber: number): { firstName: string; lastName: string } => {
    const parts = crewName.trim().split(/\s+/);
    return {
      firstName: parts[0] || `Crew ${crewNumber}`,
      lastName: parts.slice(1).join(' ') || ''
    };
  };

  const handleSaveRoutes = async () => {
    if (!selectedDate || optimizedRoutes.length === 0) {
      setError('No routes to save');
      return;
    }

    try {
      setOptimizing(true);
      setError(null);

      // Format date as YYYY-MM-DD in local timezone (not UTC)
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;

      const response = await apiService.post('/route-optimization/save-routes', {
        date: dateStr,
        routes: optimizedRoutes
      });

      if (response.success) {
        setSuccess('Routes saved successfully! Crew members can now access their assigned routes.');
      } else {
        const details = (response as any).details;
        const msg =
          (Array.isArray(details) && details.length
            ? `${response.message || response.error}\n\n${details.join('\n')}`
            : null) ||
          response.error ||
          'Failed to save routes';
        setError(msg);
      }
    } catch (err: any) {
      const rawDetails = err?.raw?.details;
      const msg =
        (Array.isArray(rawDetails) && rawDetails.length && typeof rawDetails[0] === 'string'
          ? `${err.message}\n\n${rawDetails.join('\n')}`
          : null) ||
        err.message ||
        'Failed to save routes';
      setError(msg);
    } finally {
      setOptimizing(false);
    }
  };

  // Clean function to load crew logins from API
  const loadCrewLogins = async (): Promise<CrewLogin[]> => {
    const response = await apiService.get('/route-optimization/crew-logins');
    if (response.success && Array.isArray(response.data)) {
      return normalizeCrewLogins(response.data);
    }
    return [];
  };

  // Open crew logins dialog and load data
  const handleViewCrewLogins = async () => {
    try {
      setLoadingCrewLogins(true);
      setError(null);
      setCrewLoginsDialog({ open: true });
      
      const logins = await loadCrewLogins();
      setCrewLogins(logins);
    } catch (err: any) {
      setError(err.message || 'Failed to load crew logins');
    } finally {
      setLoadingCrewLogins(false);
    }
  };

  // Clean save function - save and refresh from API
  const handleSaveCredentials = async (crewId: string, crewName?: string, password?: string) => {
    try {
      setSavingCredentials(true);
      setError(null);
      setSuccess(null);

      const normalizedCrewId = crewId.startsWith('crew-') ? crewId : `crew-${crewId.match(/(\d+)/)?.[1] || '1'}`;
      const payload: Record<string, string> = {};
      if (crewName?.trim()) payload.crewName = crewName.trim();
      if (password?.trim()) payload.password = password.trim();

      if (Object.keys(payload).length === 0) {
        setError('Enter a crew name or password to update.');
        return;
      }

      const response = await apiService.post(
        `/route-optimization/crew-logins/${normalizedCrewId}/update-credentials`,
        payload
      );

      if (response.success) {
        // Always refresh from API to get latest data (including all passwords)
        const refreshedLogins = await loadCrewLogins();
        setCrewLogins(refreshedLogins);
        
        // Update existing crews cache
        const crewsMap: Record<number, { firstName: string; lastName: string; crewName: string }> = {};
        refreshedLogins.forEach((crew: CrewLogin) => {
          crewsMap[crew.crewNumber] = {
            firstName: crew.firstName || `Crew ${crew.crewNumber}`,
            lastName: crew.lastName || '',
            crewName: `${crew.firstName || `Crew ${crew.crewNumber}`} ${crew.lastName || ''}`.trim()
          };
        });
        setExistingCrews(prev => ({ ...prev, ...crewsMap }));
        
        setSuccess(`Credentials saved for ${normalizedCrewId}.`);
        setEditingLogin(null);
      } else {
        setError(response.error || 'Failed to save credentials');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save credentials');
    } finally {
      setSavingCredentials(false);
    }
  };

  const formatDate = (dateInput: string | Date) => {
    // Handle both ISO strings and date objects
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    // Format in Central Time (America/Chicago)
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      timeZone: 'America/Chicago'
    });
  };

  const getServiceFrequency = (service: ScheduledService) => {
    // SINGLE SOURCE OF TRUTH: Use exact same approach as Customer Details dialog
    // Get full ExistingCustomer object from map (same data source as Customer Details)
    const serviceCategory = service.serviceType?.category;
    
    // Map service category to the format expected by getServiceFrequencyUtil
    let category: 'mowing' | 'fertilizing' | 'snow_removal' | null = null;
    if (serviceCategory === 'mowing') {
      category = 'mowing';
    } else if (serviceCategory === 'fertilizing') {
      category = 'fertilizing';
    } else if (serviceCategory === 'snow_removal') {
      category = 'snow_removal';
    }
    
    if (!category) {
      // Fallback for unknown categories
      if (service.frequency === 'weekly') return 'Weekly';
      if (service.frequency === 'bi-weekly') return 'Bi-weekly';
      if (service.frequency === 'monthly') return 'Monthly';
      if (service.frequency === 'per-instance') return 'Per Instance';
      if (service.frequency === 'seasonal-contract') return 'Seasonal Contract';
      return 'One-time';
    }
    
    // Get customer ID (could be _id or customerId._id)
    // For onboarded customers, service.customerId might be the linkedCustomerId
    const customerId = (service.customerId as any)?._id || (service.customerId as any)?.id || service.customerId;
    const customerIdStr = customerId ? customerId.toString() : null;
    
    // Get full ExistingCustomer object from map (same as Customer Details uses)
    // This will find the customer by either ExistingCustomer._id or linkedCustomerId
    let customer = customerIdStr ? existingCustomersMap.get(customerIdStr) : null;
    
    // If not found, try to find by iterating through the map to check linkedCustomerId
    // This handles cases where the service.customerId is a linkedCustomerId but wasn't mapped
    if (!customer && customerIdStr) {
      // Convert Map.entries() to array for compatibility with es5 target
      for (const [mapKey, mapCustomer] of Array.from(existingCustomersMap.entries())) {
        const linkedId = mapCustomer.linkedCustomerId?._id || mapCustomer.linkedCustomerId?.id || mapCustomer.linkedCustomerId;
        if (linkedId && linkedId.toString() === customerIdStr) {
          customer = mapCustomer;
          console.log('[ROUTE-OPT-FREQUENCY] ✅ Found customer by linkedCustomerId:', {
            customerIdStr,
            mapKey,
            customerName: mapCustomer.firstName + ' ' + mapCustomer.lastName
          });
          break;
        }
      }
    }
    
    // Log full customer object structure to diagnose
    console.log('[ROUTE-OPT-FREQUENCY] Customer lookup:', {
      serviceId: service._id,
      customerName: `${service.customerId.firstName} ${service.customerId.lastName}`,
      customerIdStr,
      foundCustomer: !!customer,
      mapSize: existingCustomersMap.size,
      customerHasServicePricing: !!customer?.servicePricing,
      customerServicePricingType: typeof customer?.servicePricing,
      customerServicePricingKeys: customer?.servicePricing ? Object.keys(customer.servicePricing) : [],
      customerServicePricingFull: customer?.servicePricing, // Log full object
      mowingPricingFull: customer?.servicePricing?.lawnMowingTrimming, // Log full mowing pricing
      mowingPricing: customer?.servicePricing?.lawnMowingTrimming ? {
        frequency: customer.servicePricing.lawnMowingTrimming.frequency,
        hasSeasonalContractDetails: !!customer.servicePricing.lawnMowingTrimming.seasonalContractDetails,
        serviceFrequency: customer.servicePricing.lawnMowingTrimming.seasonalContractDetails?.serviceFrequency,
        fullObject: customer.servicePricing.lawnMowingTrimming
      } : null,
      customerFull: customer // Log full customer object for debugging
    });
    
    if (!customer && customerIdStr) {
      console.warn('[ROUTE-OPT-FREQUENCY] ⚠️ Customer not found in map:', {
        serviceId: service._id,
        customerIdStr,
        customerName: `${service.customerId.firstName} ${service.customerId.lastName}`,
        mapSize: existingCustomersMap.size,
        mapKeys: Array.from(existingCustomersMap.keys()).slice(0, 10), // Show first 10 keys for debugging
        serviceCustomerIdStructure: {
          has_id: !!(service.customerId as any)?._id,
          has_id_value: (service.customerId as any)?._id,
          has_id_string: (service.customerId as any)?._id?.toString(),
          has_id_field: !!(service.customerId as any)?.id,
          has_id_field_value: (service.customerId as any)?.id,
          customerIdType: typeof service.customerId,
          customerIdValue: service.customerId
        }
      });
    }
    
    // Use the EXACT same utility function as Customer Details (single source of truth)
    // CRITICAL: Pass the service object as serviceRequest (same as Customer Details does)
    // The service object IS the serviceRequest - it has acceptedBid, frequency, etc.
    const frequency = getServiceFrequencyUtil(customer, category, service);
    
    console.log('[ROUTE-OPT-FREQUENCY] Utility function result:', {
      serviceId: service._id,
      customerName: `${service.customerId.firstName} ${service.customerId.lastName}`,
      category,
      frequency,
      hadCustomer: !!customer,
      serviceFrequency: service.frequency,
      hasAcceptedBid: !!service.acceptedBidId,
      hasAcceptedBidServicePricing: !!(service.acceptedBidId as any)?.servicePricing
    });
    
    // CRITICAL FALLBACK: If utility returns 'seasonal-contract' but we have service data,
    // check service.acceptedBidId.servicePricing directly (same logic Customer Details uses)
    if (frequency === 'seasonal-contract' && (service.acceptedBidId as any)?.servicePricing) {
      const servicePricing = (service.acceptedBidId as any).servicePricing;
      const matchingService = servicePricing.find((sp: any) => {
        if (category === 'mowing') return sp.service === 'lawnMowingTrimming' || sp.serviceName?.toLowerCase().includes('mowing');
        if (category === 'fertilizing') return sp.service === 'weedControlFertilizing' || sp.serviceName?.toLowerCase().includes('fertiliz') || sp.serviceName?.toLowerCase().includes('weed');
        if (category === 'snow_removal') return sp.service === 'snowRemoval' || sp.serviceName?.toLowerCase().includes('snow');
        return false;
      });
      
      if (matchingService?.seasonalContractDetails?.serviceFrequency) {
        const serviceFreq = matchingService.seasonalContractDetails.serviceFrequency;
        if (serviceFreq !== 'seasonal-contract') {
          console.log('[ROUTE-OPT-FREQUENCY] ✅ Found serviceFrequency in acceptedBid.servicePricing:', {
            serviceId: service._id,
            customerName: `${service.customerId.firstName} ${service.customerId.lastName}`,
            serviceFrequency: serviceFreq
          });
          // Format and return
          if (serviceFreq === 'per-instance') return 'Per Instance';
          if (serviceFreq === 'weekly') return 'Weekly';
          if (serviceFreq === 'bi-weekly') return 'Bi-Weekly';
          if (serviceFreq === 'monthly') return 'Monthly';
          return serviceFreq.charAt(0).toUpperCase() + serviceFreq.slice(1).replace('-', '-');
        }
      }
    }
    
    if (!frequency) {
      // Fallback if utility function returns null
      if (service.frequency === 'weekly') return 'Weekly';
      if (service.frequency === 'bi-weekly') return 'Bi-weekly';
      if (service.frequency === 'monthly') return 'Monthly';
      if (service.frequency === 'per-instance') return 'Per Instance';
      if (service.frequency === 'seasonal-contract') return 'Seasonal Contract';
      return 'One-time';
    }
    
    // Format the frequency for display (match Customer Details format)
    if (frequency === 'per-instance') return 'Per Instance';
    if (frequency === 'weekly') return 'Weekly';
    if (frequency === 'bi-weekly') return 'Bi-Weekly';
    if (frequency === 'monthly') return 'Monthly';
    if (frequency === 'seasonal-contract') return 'Seasonal Contract';
    
    // Capitalize first letter for any other frequency
    return frequency.charAt(0).toUpperCase() + frequency.slice(1).replace('-', '-');
  };

  // Helper to check if a service is already in an optimized route
  const isServiceInRoute = (service: ScheduledService): { inRoute: boolean; routeName?: string } => {
    const serviceCustomerId = service.customerId?._id || service.customerId;
    if (!serviceCustomerId) return { inRoute: false };
    
    const customerIdStr = serviceCustomerId.toString();
    
    for (const route of optimizedRoutes) {
      const jobInRoute = route.jobs.find((job: any) => {
        const jobCustomerId = job.customerId?._id || job.customerId;
        return jobCustomerId ? jobCustomerId.toString() === customerIdStr : false;
      });
      
      if (jobInRoute) {
        return { inRoute: true, routeName: route.crewName || route.crewId };
      }
    }
    
    return { inRoute: false };
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h2" sx={{ display: 'inline-flex', alignItems: 'center' }}>
          Route Optimization
          <InfoTip title="Assign a day’s stops to crews, optimize drive order, then Save Routes for the crew app." />
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {selectedDate && formatDate(selectedDate)}
        </Typography>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Date Selection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4} data-tour="demo-routes-date">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TextField
                  fullWidth
                  label="Select Date"
                  type="date"
                  value={selectedDate ? (() => {
                    // Format date as YYYY-MM-DD in local timezone (not UTC)
                    const year = selectedDate.getFullYear();
                    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
                    const day = String(selectedDate.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                  })() : ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      // Parse date string as local date (not UTC)
                      const [year, month, day] = e.target.value.split('-').map(Number);
                      // Create date at midnight in local timezone
                      const newDate = new Date(year, month - 1, day, 0, 0, 0, 0);
                      setSelectedDate(newDate);
                    } else {
                      setSelectedDate(null);
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                />
                <InfoTip title="Pick the day you’re building. Habit: plan Mon–Sat once so crews have saved routes all week." />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <FormControl fullWidth>
                  <InputLabel>Number of Crews</InputLabel>
                  <Select
                    value={crewSize}
                    onChange={(e) => setCrewSize(Number(e.target.value))}
                    label="Number of Crews"
                  >
                    {[1, 2, 3, 4, 5, 6].map(num => (
                      <MenuItem key={num} value={num}>{num} Crew{num > 1 ? 's' : ''}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <InfoTip title="How many crews to split today’s stops across (must match crews set up in Crew Management)." />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3} data-tour="demo-routes-sort">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={handleSortServices}
                  disabled={optimizing || scheduledServices.length === 0}
                  startIcon={optimizing ? <CircularProgress size={20} color="inherit" /> : <Schedule />}
                  sx={{ height: '56px', fontSize: '0.875rem' }}
                >
                  {optimizing ? 'Working...' : 'Sort and Optimize'}
                </Button>
                <InfoTip title="Assigns today’s stops to crews by service type, then optimizes drive order." />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={2} data-tour="demo-routes-reoptimize">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleOptimizeRoutes}
                  disabled={optimizing || optimizedRoutes.length === 0 || scheduledServices.length === 0}
                  startIcon={optimizing ? <CircularProgress size={20} /> : <Route />}
                  sx={{ height: '56px', fontSize: '0.875rem' }}
                >
                  {optimizing ? 'Re-optimizing...' : 'Re-optimize'}
                </Button>
                <InfoTip title="After you move stops between crews, reorder drive sequence without re-sorting assignments." />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleViewCrewLogins}
                  disabled={loadingCrewLogins}
                  startIcon={<Visibility />}
                  sx={{ height: '56px', fontSize: '0.875rem' }}
                >
                  View Crew Logins
                </Button>
                <InfoTip title="Show crew app usernames/passwords generated in Crew Management." />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3} data-tour="demo-routes-save">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={handleSaveRoutes}
                  disabled={optimizing || optimizedRoutes.length === 0}
                  startIcon={optimizing ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
                  sx={{ height: '56px', fontSize: '0.875rem' }}
                >
                  {optimizing ? 'Saving...' : 'Save Routes'}
                </Button>
                <InfoTip title="Lock the day for crew logins. Change the date and repeat for the rest of the week." />
              </Box>
            </Grid>
          </Grid>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Assign service types and generate crew logins in Crew Management. Sort and Optimize assigns today’s stops then optimizes drive order. Re-optimize reorders after you move stops — it does not re-sort.
          </Typography>
        </CardContent>
      </Card>

      {/* Scheduled Services for Selected Date */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Services for {selectedDate ? formatDate(selectedDate) : 'Selected Date'}
            </Typography>
            <Badge badgeContent={scheduledServices.length} color="primary">
              <Schedule />
            </Badge>
          </Box>
          
          {loadingServices ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : scheduledServices.length === 0 ? (
            <Alert severity="info">
              No services found for {selectedDate ? formatDate(selectedDate) : 'the selected date'}. 
              Please accept some bids or schedule services first.
            </Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      Customer
                      <InfoTip title="Who the stop is for." />
                    </TableCell>
                    <TableCell>
                      Address
                      <InfoTip title="Stop location used for drive-order optimization." />
                    </TableCell>
                    <TableCell>
                      Service
                      <InfoTip title="Service type — Sort and Optimize uses this with crew assignments." />
                    </TableCell>
                    <TableCell>
                      Status
                      <InfoTip title="Scheduled vs needs scheduling; payment hold means crews shouldn’t complete until resolved." />
                    </TableCell>
                    <TableCell>
                      Frequency
                      <InfoTip title="How often this service recurs (weekly, biweekly, etc.)." />
                    </TableCell>
                    <TableCell>
                      Duration
                      <InfoTip title="Estimated time on site — helps balance crew workloads." />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scheduledServices
                    .filter((service) => service.status !== 'completed') // Filter out completed from table display
                    .map((service) => (
                    <TableRow key={service._id}>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2">
                            {service.customerId.firstName} {service.customerId.lastName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {service.customerId.email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {service.address.street}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {service.address.city}, {service.address.state} {service.address.zipCode}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {getServiceDisplayName(service.serviceType)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                          {service.isOnPaymentHold ? (
                            <Tooltip title={
                              service.onHoldReason?.toLowerCase().includes('payment failed') || 
                              service.onHoldReason?.toLowerCase().includes('card declined') ||
                              service.onHoldReason?.toLowerCase().includes('declined') ||
                              service.onHoldReason?.toLowerCase().includes('insufficient funds')
                                ? `Payment failed - ${service.onHoldReason || 'Payment processing failed. Service on hold until payment is resolved.'}`
                                : service.onHoldReason || 'No payment method set up - Customer removed payment method'
                            }>
                              <Chip 
                                icon={<Warning />}
                                label={
                                  service.onHoldReason?.toLowerCase().includes('payment failed') || 
                                  service.onHoldReason?.toLowerCase().includes('card declined') ||
                                  service.onHoldReason?.toLowerCase().includes('declined') ||
                                  service.onHoldReason?.toLowerCase().includes('insufficient funds')
                                    ? 'Payment Hold'
                                    : 'No Payment Setup'
                                }
                                size="small"
                                color="error"
                                variant="outlined"
                              />
                            </Tooltip>
                          ) : (
                            <Chip 
                              label={service.scheduledDate ? 'Scheduled' : 'Needs Scheduling'} 
                              size="small" 
                              color={service.scheduledDate ? 'success' : 'warning'}
                            />
                          )}
                          {(() => {
                            const routeInfo = isServiceInRoute(service);
                            if (routeInfo.inRoute) {
                              return (
                                <Chip 
                                  label={`In ${routeInfo.routeName}`}
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                />
                              );
                            }
                            return null;
                          })()}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={getServiceFrequency(service)} 
                          size="small" 
                          color={getServiceFrequency(service).includes('Weekly') ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        {(() => {
                          // Display duration in hours (estimatedDuration is stored in hours)
                          // Use service.estimatedDuration directly from ServiceRequest (single source of truth)
                          const duration = service.estimatedDuration;
                          
                          // Only display if duration exists and is reasonable (0.25 to 8 hours; 15 min increments)
                          if (duration && duration >= 0.25 && duration <= 8) {
                            const hours = duration;
                            const hoursText = hours === 1 ? '1 hr' : `${hours} hrs`;
                            return (
                              <Typography variant="body2">
                                {hoursText}
                              </Typography>
                            );
                          }
                          return (
                            <Typography variant="body2" color="text.secondary">
                              N/A
                            </Typography>
                          );
                        })()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Optimized Routes */}
      {optimizedRoutes.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box>
                <Typography variant="h6">
                  Optimized Routes
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Routes have been optimized and divided among {optimizedRoutes.length} crew{optimizedRoutes.length > 1 ? 's' : ''}.
                </Typography>
              </Box>
              <Box display="flex" gap={1} flexWrap="wrap" alignItems="center" sx={{ minWidth: 0 }}>
                {/* View Crew Logins Button */}
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  onClick={handleViewCrewLogins}
                  startIcon={<Visibility />}
                  disabled={loadingCrewLogins}
                  title="View existing crew login credentials"
                  sx={{ 
                    minWidth: '180px',
                    py: 1.5,
                    px: 3
                  }}
                >
                  View Crew Logins
                </Button>
                <InfoTip title="Show crew app usernames/passwords generated in Crew Management." />
                {/* Save Routes Button */}
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleSaveRoutes}
                  startIcon={optimizing ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
                  disabled={optimizing}
                  title="Save optimized routes for crew access"
                  sx={{ 
                    minWidth: '160px',
                    py: 1.5,
                    px: 3,
                    boxShadow: 2
                  }}
                >
                  {optimizing ? 'Saving...' : 'Save Routes'}
                </Button>
                <InfoTip title="Lock the day for crew logins. Change the date and repeat for the rest of the week." />
              </Box>
            </Box>
            
            {optimizedRoutes.map((route, index) => (
              <Accordion key={route.crewId} sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box display="flex" alignItems="center" width="100%">
                    <People sx={{ mr: 1 }} />
                    {editingCrewName === route.crewId ? (
                      <TextField
                        value={route.crewName ?? ''}
                        onChange={(e) => {
                          // Allow empty string - user can clear the field completely
                          const updatedRoutes = optimizedRoutes.map(r => 
                            r.crewId === route.crewId ? { ...r, crewName: e.target.value } : r
                          );
                          setOptimizedRoutes(updatedRoutes);
                        }}
                        onBlur={() => {
                          // If empty, revert to original crewId, otherwise save the new name
                          const nameToSave = (route.crewName?.trim() || '') === '' 
                            ? route.crewId 
                            : route.crewName;
                          handleUpdateCrewName(route.crewId, nameToSave);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            // If empty, revert to original crewId, otherwise save the new name
                            const nameToSave = (route.crewName?.trim() || '') === '' 
                              ? route.crewId 
                              : route.crewName;
                            handleUpdateCrewName(route.crewId, nameToSave);
                          } else if (e.key === 'Escape') {
                            // Revert to original name on escape
                            const updatedRoutes = optimizedRoutes.map(r => 
                              r.crewId === route.crewId ? { ...r, crewName: route.crewId } : r
                            );
                            setOptimizedRoutes(updatedRoutes);
                            setEditingCrewName(null);
                          }
                        }}
                        autoFocus
                        size="small"
                        sx={{ minWidth: '120px', maxWidth: '200px' }}
                      />
                    ) : (
                      <Typography 
                        variant="subtitle1"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingCrewName(route.crewId);
                        }}
                        sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                      >
                        {route.crewName || route.crewId}
                      </Typography>
                    )}
                    <Box ml="auto" display="flex" gap={2}>
                      <Chip 
                        label={`${route.jobs.filter((j) => shouldDisplayJob(j)).length} stops`} 
                        size="small" 
                        color="primary" 
                      />
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {route.jobs
                      .filter((job) => shouldDisplayJob(job)) // Automatically filter out rescheduled jobs (but keep completed)
                      .map((job, jobIndex) => {
                        // Check if this job appears multiple times in the route
                        const filteredJobs = route.jobs.filter((j) => shouldDisplayJob(j));
                        const duplicateIndices = filteredJobs
                          .map((j, idx) => j.customerId === job.customerId ? idx : -1)
                          .filter(idx => idx !== -1);
                        const isDuplicate = duplicateIndices.length > 1;
                        // Gray out if it's a duplicate and not the last occurrence
                        const isGrayedOut = isDuplicate && jobIndex !== duplicateIndices[duplicateIndices.length - 1];
                        const isCompleted = isJobCompleted(job);
                        
                        return (
                          <ListItem 
                            key={`${job.customerId}-${jobIndex}`}
                            sx={{
                              opacity: isGrayedOut ? 0.5 : 1,
                              backgroundColor: isGrayedOut ? 'action.hover' : 'transparent',
                              textDecoration: isGrayedOut ? 'line-through' : 'none'
                            }}
                          >
                            <ListItemText
                              primary={
                                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                  <Typography variant="subtitle2">
                                    {jobIndex + 1}. {job.customerName}
                                  </Typography>
                                  {isCompleted && (
                                    <Chip 
                                      label="Completed" 
                                      size="small" 
                                      color="success"
                                      sx={{ height: 18, fontSize: '0.65rem' }}
                                    />
                                  )}
                                  {isGrayedOut && (
                                    <Chip 
                                      label="Duplicate" 
                                      size="small" 
                                      color="warning"
                                      sx={{ height: 18, fontSize: '0.65rem' }}
                                    />
                                  )}
                                </Box>
                              }
                              secondary={
                                <Box>
                                  <Box display="flex" gap={0.5} flexWrap="wrap" mb={0.5}>
                                    {getServiceNames(job).map((serviceName, idx) => (
                                      <Chip
                                        key={idx}
                                        label={serviceName}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        sx={{ 
                                          height: 20, 
                                          fontSize: '0.7rem',
                                          fontWeight: 500
                                        }}
                                      />
                                    ))}
                                  </Box>
                                  <Typography variant="body2" color="text.secondary">
                                    {job.address}
                                  </Typography>
                                </Box>
                              }
                            />
                            <ListItemSecondaryAction>
                              <Box display="flex" gap={0.5}>
                                <Tooltip title={isCompleted ? "Cannot move completed service" : isGrayedOut ? "This is a duplicate - will be removed when saved" : "Move to another route"}>
                                  <span>
                                    <IconButton 
                                      size="small" 
                                      onClick={() => handleMoveJob(job, route, jobIndex)}
                                      disabled={isGrayedOut || isCompleted}
                                    >
                                      <Edit />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title={isCompleted ? "Cannot delete completed service" : "Remove from route"}>
                                  <span>
                                    <IconButton 
                                      size="small" 
                                      onClick={() => handleDeleteJob(job, route)}
                                      color="error"
                                      disabled={isCompleted}
                                    >
                                      <Delete />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            </ListItemSecondaryAction>
                          </ListItem>
                        );
                      })}
                  </List>
                </AccordionDetails>
              </Accordion>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Completed Routes */}
      {completedRoutes.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Completed Routes
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Historical routes and service stops from previous days.
            </Typography>
            
            {completedRoutes.map((route) => (
              <Accordion key={route._id} sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box display="flex" alignItems="center" width="100%">
                    <CheckCircle sx={{ mr: 1, color: 'success.main' }} />
                    <Typography variant="subtitle1">
                      {route.crewId} - {formatDate(route.date)}
                    </Typography>
                    <Box ml="auto" display="flex" gap={2}>
                      <Chip 
                        label={`${route.jobs.filter(job => job.completed).length}/${route.jobs.length} completed`} 
                        size="small" 
                        color="success"
                      />
                      <Chip label={`${route.jobs.length} stops`} size="small" />
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {route.jobs.map((job, jobIndex) => (
                      <ListItem key={job.customerId}>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center">
                              <Typography variant="subtitle2">
                                {jobIndex + 1}. {job.customerName}
                              </Typography>
                              {job.completed ? (
                                <CheckCircle sx={{ ml: 1, color: 'success.main', fontSize: 16 }} />
                              ) : (
                                <Cancel sx={{ ml: 1, color: 'error.main', fontSize: 16 }} />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {job.address}
                              </Typography>
                              {job.actualArrival && (
                                <Typography variant="caption" color="text.secondary">
                                  Arrived: {job.actualArrival}
                                  {job.actualDeparture && ` | Departed: ${job.actualDeparture}`}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Move Job Dialog */}
      <Dialog open={moveJobDialog.open} onClose={() => setMoveJobDialog({ open: false, job: null, fromRoute: null, targetRouteIndex: 0, targetPosition: '1' })} maxWidth="sm" fullWidth>
        <DialogTitle>
          Move to Another Route
          {moveJobDialog.job && (
            <Typography variant="subtitle1" color="text.secondary">
              {moveJobDialog.job.customerName}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Target Route</InputLabel>
                <Select
                  value={moveJobDialog.targetRouteIndex}
                  onChange={(e) => setMoveJobDialog({ ...moveJobDialog, targetRouteIndex: Number(e.target.value) })}
                >
                  {optimizedRoutes.map((route, idx) => (
                    <MenuItem key={idx} value={idx}>
                      {route.crewName || route.crewId} ({route.jobs.length} stops)
                      {route.crewId === moveJobDialog.fromRoute?.crewId && ' (same route)'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={`Position in Route (1-${optimizedRoutes[moveJobDialog.targetRouteIndex]?.jobs.length + 1 || 'N/A'})`}
                type="number"
                value={moveJobDialog.targetPosition}
                onChange={(e) => setMoveJobDialog({ ...moveJobDialog, targetPosition: e.target.value })}
                helperText="Enter the desired position in the route (1 = first stop)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveJobDialog({ open: false, job: null, fromRoute: null, targetRouteIndex: 0, targetPosition: '1' })}>
            Cancel
          </Button>
          <Button onClick={submitMoveJob} variant="contained">
            Move Job
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reschedule Dialog */}
      <Dialog open={rescheduleDialog.open} onClose={() => setRescheduleDialog({ open: false, service: null, newDate: '', newTime: '', changeType: 'one-time' })} maxWidth="sm" fullWidth>
        <DialogTitle>
          Reschedule Service
          {rescheduleDialog.service && (
            <Typography variant="subtitle1" color="text.secondary">
              {rescheduleDialog.service.customerId?.firstName || ''} {rescheduleDialog.service.customerId?.lastName || ''}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="New Scheduled Date"
                type="date"
                value={rescheduleDialog.newDate}
                onChange={(e) => setRescheduleDialog({ ...rescheduleDialog, newDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Change Type</InputLabel>
                <Select
                  value={rescheduleDialog.changeType}
                  onChange={(e) => setRescheduleDialog({ ...rescheduleDialog, changeType: e.target.value as 'one-time' | 'permanent' })}
                  label="Change Type"
                >
                  <MenuItem value="one-time">
                    One-Time: Only reschedule this single service date
                  </MenuItem>
                  <MenuItem value="permanent">
                    Permanent: Update scheduled day for all future services
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes (Optional)"
                multiline
                rows={3}
                value={rescheduleDialog.service?.notes || ''}
                onChange={(e) => {
                  if (rescheduleDialog.service) {
                    rescheduleDialog.service.notes = e.target.value;
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRescheduleDialog({ open: false, service: null, newDate: '', newTime: '', changeType: 'one-time' })}>
            Cancel
          </Button>
          <Button onClick={submitReschedule} variant="contained">
            Reschedule Service
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign to Route Dialog */}
      <Dialog 
        open={assignToRouteDialog.open} 
        onClose={() => setAssignToRouteDialog({ open: false, service: null, selectedRouteId: '' })} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          Assign Service to Route
          {assignToRouteDialog.service && (
            <Typography variant="subtitle1" color="text.secondary">
              {assignToRouteDialog.service.customerId.firstName} {assignToRouteDialog.service.customerId.lastName} - {getServiceDisplayName(assignToRouteDialog.service.serviceType)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Select Route/Crew</InputLabel>
                <Select
                  value={assignToRouteDialog.selectedRouteId}
                  onChange={(e) => setAssignToRouteDialog({ ...assignToRouteDialog, selectedRouteId: e.target.value })}
                  label="Select Route/Crew"
                >
                  {optimizedRoutes.map((route) => (
                    <MenuItem key={route.crewId} value={route.crewId}>
                      {route.crewName} ({route.jobs.length} {route.jobs.length === 1 ? 'stop' : 'stops'})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {assignToRouteDialog.service && (
              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="body2">
                    This service will be added to the selected route at the end of the route. 
                    The route order will not be re-optimized.
                  </Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignToRouteDialog({ open: false, service: null, selectedRouteId: '' })}>
            Cancel
          </Button>
          <Button 
            onClick={handleAssignToRoute} 
            variant="contained"
            disabled={!assignToRouteDialog.selectedRouteId}
          >
            Assign to Route
          </Button>
        </DialogActions>
      </Dialog>

      {/* Crew Logins Dialog */}
      <Dialog 
        open={crewLoginsDialog.open} 
        onClose={() => {
          setCrewLoginsDialog({ open: false });
          setError(null);
          setSuccess(null);
          setEditingLogin(null);
        }} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Crew Login Credentials</Typography>
            <Button
              size="small"
              onClick={() => {
                setCrewLoginsDialog({ open: false });
                setError(null);
                setSuccess(null);
                setEditingLogin(null);
              }}
            >
              Close
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}
          <Alert severity="info" sx={{ mb: 2 }}>
            Each crew member has their own login credentials. Crew members can use these credentials to access their assigned routes on the crew dashboard.
          </Alert>
          
          {loadingCrewLogins ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : crewLogins.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No crew logins found. Create them in Crew Management → Crews (View &amp; Edit Crew Logins).
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Crew</strong></TableCell>
                    <TableCell><strong>Username</strong></TableCell>
                    <TableCell><strong>Password</strong></TableCell>
                    <TableCell><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {crewLogins.map((login) => {
                    const crewId = login.crewId || `crew-${login.crewNumber}`;
                    const displayName = `${login.firstName || ''} ${login.lastName || ''}`.trim() || `Crew ${login.crewNumber}`;
                    const isEditing = editingLogin?.crewId === crewId;
                    // When editing, always use the value from editingLogin (can be empty string)
                    // If editingLogin.crewName is undefined, it means we just started editing, so use displayName
                    // But once set, even if empty string, use that value
                    const editingCrewNameValue = isEditing 
                      ? (editingLogin?.crewName ?? displayName)
                      : displayName;
                    const editingPasswordValue = isEditing ? editingLogin?.password ?? '' : '';
                    const trimmedCrewNameValue = (editingCrewNameValue ?? '').trim();
                    // Crew name is changed if it's different from display name, or if empty (will revert to original)
                    const crewNameChanged =
                      isEditing && trimmedCrewNameValue !== displayName;
                    const passwordProvided = Boolean(editingPasswordValue?.trim());
                    const saveDisabled = savingCredentials || (!crewNameChanged && !passwordProvided);

                    return (
                      <TableRow key={crewId}>
                        <TableCell>
                          {isEditing ? (
                            <TextField
                              size="small"
                              fullWidth
                              label="Crew name"
                              value={editingLogin?.crewName ?? ''}
                              onChange={(e) => {
                                // Immediately update state with whatever the user types
                                // This includes empty strings, partial text, etc.
                                // Use functional update to ensure we have latest state
                                setEditingLogin(prev => {
                                  if (prev && prev.crewId === crewId) {
                                    return { 
                                      ...prev, 
                                      crewName: e.target.value
                                    };
                                  }
                                  return prev;
                                });
                              }}
                              helperText="Leave empty to revert to original name. Updating the crew name also updates the login handle."
                              autoFocus
                            />
                          ) : (
                            <>
                              <Typography variant="body2" fontWeight="medium">
                                {displayName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {crewId}
                              </Typography>
                            </>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2">{login.email}</Typography>
                            <Tooltip title="Copy username">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  navigator.clipboard.writeText(login.email);
                                  setSuccess('Username copied to clipboard');
                                }}
                              >
                                <ContentCopy fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          {isEditing && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              Username updates automatically when you save a new crew name.
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <TextField
                              size="small"
                              fullWidth
                              type="text"
                              label="New password (optional)"
                              value={editingPasswordValue}
                              onChange={(e) => {
                                if (editingLogin) {
                                  setEditingLogin({ ...editingLogin, password: e.target.value });
                                }
                              }}
                              helperText="Leave blank to keep the current password"
                            />
                          ) : login.password ? (
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: 'monospace',
                                  backgroundColor: 'grey.100',
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  fontWeight: 'bold',
                                  minWidth: '100px'
                                }}
                              >
                                {login.password}
                              </Typography>
                              <Tooltip title="Copy password">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    navigator.clipboard.writeText(login.password!);
                                    setSuccess('Password copied to clipboard');
                                  }}
                                >
                                  <ContentCopy fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {login.message || 'Password unavailable. Click Reset to create a new one.'}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <Box display="flex" gap={1}>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => {
                                  if (editingLogin) {
                                    // If crew name is empty or just whitespace, revert to original display name
                                    const crewNameToSave = (editingLogin.crewName?.trim() || '') === '' 
                                      ? displayName 
                                      : editingLogin.crewName;
                                    handleSaveCredentials(
                                      crewId,
                                      crewNameToSave,
                                      editingLogin.password
                                    );
                                  }
                                }}
                                disabled={saveDisabled}
                              >
                                {savingCredentials ? 'Saving...' : 'Save'}
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setEditingLogin(null)}
                                disabled={savingCredentials}
                              >
                                Cancel
                              </Button>
                            </Box>
                          ) : (
                            <Box display="flex" gap={1}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                  // Initialize with current display name as a string
                                  setEditingLogin({
                                    crewId,
                                    crewName: displayName || `Crew ${login.crewNumber}`,
                                    password: ''
                                  });
                                }}
                                startIcon={<Edit />}
                              >
                                Edit
                              </Button>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          
          {crewLogins.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info">
                <Typography variant="body2" fontWeight="bold">
                  Crew Login Credentials
                </Typography>
                <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                  • Passwords are saved and always visible to providers<br />
                  • Login URL: {CREW_LOGIN_URL}
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCrewLoginsDialog({ open: false });
            setEditingLogin(null);
            setError(null);
            setSuccess(null);
          }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default RouteOptimizationDashboard;